import type {
  AutomationConditionNode,
  AutomationConditionOperator,
  AutomationConditionValue,
  AutomationDataSourceContract,
  AutomationDependencyPolicy,
  AutomationStopPolicy,
  AutomationSuppressionPolicy,
  AutomationTimingAnchor,
  AutomationTriggerContract,
  AutomationWorkflow,
  AutomationWorkflowCreatePayload,
  AutomationWorkflowPayload,
  AutomationWorkflowSetting,
  AutomationWorkflowStep,
  AutomationWorkflowStepContract,
  CommunicationMediaAssetContract,
  GeneralSettingsGroup,
  GeneralSettingsGroupPayload,
  GeneralSettingsResponse,
  IntegrationSetting,
  TemplateBindingSource,
  TemplateComponentAddress,
  TemplateParameterBinding,
  TemplateParameterType,
  WorkflowChannel,
  WorkflowDelayUnit,
  WorkflowManualPolicy,
} from '@ferty9/types';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type Dispatch, type KeyboardEvent, type SetStateAction } from 'react';
import {
  activateAutomationWorkflow,
  activateAutomationDataSource,
  activateCommunicationMediaAsset,
  cancelCommunicationMessage,
  createAutomationDataSource,
  createCommunicationMediaAsset,
  deactivateAutomationDataSource,
  deactivateAutomationWorkflow,
  deactivateCommunicationMediaAsset,
  deleteAutomationDataSource,
  getAutomationWorkflowPendingImpact,
  getAutomationWorkflowTrigger,
  getAutomationDataSources,
  getCommunicationAudit,
  getCommunicationAuditTimeline,
  getCommunicationMediaAssets,
  listAutomationTemplates,
  listLookerSources,
  previewAutomationTemplate,
  previewAutomationWorkflowRows,
  previewAutomationWorkflowSchedule,
  previewAutomationWorkflowTemplate,
  retryCommunicationMessage,
  saveAutomationWorkflowTrigger,
  testAutomationDataSource,
  testLookerSource,
  updateAutomationDataSource,
  updateCommunicationMediaAsset,
  validateAutomationTemplateMapping,
  validateAutomationWorkflow,
  validateCommunicationMediaAsset,
  type AutomationDataSourceSummary,
  type AutomationTemplateSummary,
  type CommunicationAuditResponse,
  type CommunicationAuditRow,
  type CommunicationAuditTimeline,
  type CommunicationMediaAssetSummary,
  type ConfiguredLookerFieldPreview,
  type ConfiguredLookerSourceSummary,
  type ConfiguredLookerSourceTestResponse,
  type WorkflowPendingImpactResponse,
  type WorkflowSchedulePreviewResponse,
  type WorkflowTriggerResponse,
  type WorkflowValidationResponse,
} from '../lib/api/general-settings';
import { getCenters } from '../lib/api/centers';
import { OfferCampaignWorkflowSettings } from './OfferCampaignWorkflowSettings';
import type { AdminModalVariant } from './common';
import {
  getOfferCampaignAudit,
  getOfferCampaignEnrollments,
  listOfferCampaigns,
  overrideOfferCampaignEnrollment,
} from '../lib/api/offer-campaigns';
import {
  OfferCampaignAuditDetailDrawer,
  OfferCampaignAuditPanel,
  OfferCampaignEnrollmentDetailDrawer,
  OfferCampaignEnrollmentDrawerPanel,
  OfferCampaignOverrideEnrollmentModal,
  exportOfferCampaignAuditCsv,
  exportOfferCampaignEnrollmentCsv,
} from './OfferCampaignAuditPanels';
import type {
  OfferCampaignAdminSummary,
  OfferCampaignAdminPayload,
  OfferCampaignAuditQuery,
  OfferCampaignAuditRow,
  OfferCampaignEnrollmentSummary,
  OfferCampaignOverridePayload,
} from '../types/offer-campaigns';

type EditorMode =
  | { type: 'workflow'; workflow: AutomationWorkflow }
  | { type: 'new-workflow' }
  | { type: 'group'; group?: GeneralSettingsGroup }
  | { type: 'data-source'; source?: AutomationDataSourceSummary; lookerSource?: ConfiguredLookerSourceSummary }
  | { type: 'media-asset'; asset?: CommunicationMediaAssetSummary }
  | { type: 'media-preview'; asset: CommunicationMediaAssetSummary; usages: string[] }
  | null;

type ControlMeta = {
  control?: 'duration' | 'number' | 'toggle' | 'radio' | 'select' | 'text' | 'textarea';
  min?: number;
  max?: number;
  units?: WorkflowDelayUnit[];
  options?: Array<{ label: string; value: string }> | string[];
  section?: string;
  helpText?: string;
  stepDefaults?: {
    keyPrefix?: string;
    namePrefix?: string;
    templatePrefix?: string;
    triggerTypeFirst?: string;
    triggerTypeNext?: string;
    channel?: WorkflowChannel;
    channels?: WorkflowChannel[];
    templateSettingKey?: string;
  };
};

type CommunicationAuditFilters = {
  dateFrom: string;
  dateTo: string;
  workflowKey: string;
  stepKey: string;
  sourceKey: string;
  center: string;
  status: string;
  delivered: string;
  read: string;
  search: string;
};

const defaultCommunicationAuditFilters: CommunicationAuditFilters = {
  dateFrom: '',
  dateTo: '',
  workflowKey: 'ALL',
  stepKey: 'ALL',
  sourceKey: 'ALL',
  center: '',
  status: 'ALL',
  delivered: 'ALL',
  read: 'ALL',
  search: '',
};

interface TemplateOption {
  key: string;
  label: string;
  channel: WorkflowChannel;
  logicalCode: string;
  templateName: string;
  languageCode?: string | null;
  variableSchema?: unknown;
  metadata?: unknown;
}

type WorkflowEditorPanel = 'basic' | 'source' | 'schedule' | 'conditions' | 'messages' | 'mapping' | 'preview' | 'advanced';

const workflowEditorPanels: Array<{ key: WorkflowEditorPanel; label: string; help: string }> = [
  { key: 'basic', label: 'Basic Details', help: 'Name, group, scope and status' },
  { key: 'source', label: 'Looker Source', help: 'Look and runtime feed' },
  { key: 'schedule', label: 'Trigger & Schedule', help: 'Start field and timing' },
  { key: 'conditions', label: 'Conditions', help: 'Enrollment rules' },
  { key: 'messages', label: 'Messages', help: 'Message steps and templates' },
  { key: 'mapping', label: 'Template Mapping', help: 'Variables and media bindings' },
  { key: 'preview', label: 'Preview & Activate', help: 'Validate before enabling' },
  { key: 'advanced', label: 'Advanced Controls', help: 'Settings and policies' },
];

const workflowCategoryOptions = [
  'PATIENT_COMMUNICATION',
  'FEEDBACK_AUTOMATION',
  'SURVEY_OPERATIONAL_CONTROLS',
  'OPERATIONAL_CONTROLS',
  'APPOINTMENT_COMMUNICATION',
  'MEDICATION_COMMUNICATION',
  'CUSTOM_AUTOMATION',
];

const delayUnitOptions: WorkflowDelayUnit[] = ['SECONDS', 'MINUTES', 'HOURS', 'DAYS', 'WEEKS', 'MONTHS', 'YEARS'];

export function GeneralSettingsTab({
  active,
  data,
  canUpdate,
  onSaveWorkflow,
  onCreateWorkflow,
  onCreateOfferCampaign,
  onCopyWorkflow,
  onActivateWorkflow,
  onDeactivateWorkflow,
  onDeleteWorkflow,
  onSaveGroup,
  onDeleteGroup,
  integrationSettings = [],
  onNotify,
}: {
  active: boolean;
  data: GeneralSettingsResponse | null;
  integrationSettings?: IntegrationSetting[];
  canUpdate: boolean;
  onSaveWorkflow: (key: string, payload: AutomationWorkflowPayload) => Promise<void>;
  onCreateWorkflow: (payload: AutomationWorkflowCreatePayload) => Promise<void>;
  onCreateOfferCampaign: (payload: OfferCampaignAdminPayload & { campaignKey: string }) => Promise<void>;
  onCopyWorkflow: (key: string, payload: { key?: string; name?: string; description?: string }) => Promise<void>;
  onActivateWorkflow: (key: string) => Promise<void>;
  onDeactivateWorkflow: (key: string) => Promise<void>;
  onDeleteWorkflow: (key: string) => Promise<void>;
  onSaveGroup: (key: string | null, payload: GeneralSettingsGroupPayload) => Promise<void>;
  onDeleteGroup: (key: string) => Promise<void>;
  onNotify?: (message: string, type?: AdminModalVariant, title?: string) => void;
}) {
  const [editing, setEditing] = useState<EditorMode>(null);
  const [auditTab, setAuditTab] = useState<'settings' | 'notifications' | 'campaign' | 'enrollments'>('settings');
  const [auditPage, setAuditPage] = useState(0);
  const [auditWorkflowFilter, setAuditWorkflowFilter] = useState('');
  const [auditDateFrom, setAuditDateFrom] = useState('');
  const [auditDateTo, setAuditDateTo] = useState('');
  const [auditChangedBy, setAuditChangedBy] = useState('');
  const [notificationPage, setNotificationPage] = useState(1);
  const [notificationFilters, setNotificationFilters] = useState<CommunicationAuditFilters>(defaultCommunicationAuditFilters);
  const [notificationReload, setNotificationReload] = useState(0);
  const [notifications, setNotifications] = useState<CommunicationAuditResponse | null>(null);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationTimeline, setNotificationTimeline] = useState<CommunicationAuditTimeline | null>(null);
  const [notificationTimelineLoading, setNotificationTimelineLoading] = useState(false);
  const [sources, setSources] = useState<AutomationDataSourceSummary[]>([]);
  const [lookerSources, setLookerSources] = useState<ConfiguredLookerSourceSummary[]>([]);
  const [lookerSourceTests, setLookerSourceTests] = useState<Record<string, ConfiguredLookerSourceTestResponse>>({});
  const [testingLookerSource, setTestingLookerSource] = useState('');
  const [centers, setCenters] = useState<string[]>([]);
  const [workflowTriggers, setWorkflowTriggers] = useState<Record<string, WorkflowTriggerResponse | null>>({});
  const [mediaAssets, setMediaAssets] = useState<CommunicationMediaAssetSummary[]>([]);
  const [automationTemplates, setAutomationTemplates] = useState<AutomationTemplateSummary[]>([]);
  const [sourceTestResult, setSourceTestResult] = useState<string>('');
  const [offerCampaignRefresh, setOfferCampaignRefresh] = useState(0);
  const [offerCampaigns, setOfferCampaigns] = useState<OfferCampaignAdminSummary[]>([]);
  const [offerAuditCampaignId, setOfferAuditCampaignId] = useState('');
  const [offerAuditFilters, setOfferAuditFilters] = useState<OfferCampaignAuditQuery>({});
  const [offerAuditRows, setOfferAuditRows] = useState<OfferCampaignAuditRow[]>([]);
  const [offerEnrollmentRows, setOfferEnrollmentRows] = useState<OfferCampaignEnrollmentSummary[]>([]);
  const [selectedOfferAuditRow, setSelectedOfferAuditRow] = useState<OfferCampaignAuditRow | null>(null);
  const [selectedOfferEnrollment, setSelectedOfferEnrollment] = useState<OfferCampaignEnrollmentSummary | null>(null);
  const [offerOverrideDraft, setOfferOverrideDraft] = useState<OfferCampaignOverridePayload | null>(null);
  const [offerAuditBusy, setOfferAuditBusy] = useState('');
  const workflows = data?.workflows ?? [];
  const groups = data?.groups ?? [];
  const audit = data?.audit ?? [];
  const filteredAudit = audit.filter((entry) => {
    const created = new Date(entry.createdAt);
    if (auditWorkflowFilter && entry.workflowKey !== auditWorkflowFilter) return false;
    if (auditDateFrom && created < startOfLocalDay(auditDateFrom)) return false;
    if (auditDateTo && created > endOfLocalDay(auditDateTo)) return false;
    if (auditChangedBy && !(entry.changedBy || '').toLowerCase().includes(auditChangedBy.toLowerCase())) return false;
    return true;
  });
  const auditPageSize = 10;
  const auditPageCount = Math.max(1, Math.ceil(filteredAudit.length / auditPageSize));
  const visibleAudit = filteredAudit.slice(auditPage * auditPageSize, auditPage * auditPageSize + auditPageSize);
  const templateOptions = useMemo(() => {
    const apiOptions = automationTemplatesToOptions(automationTemplates);
    return apiOptions.length ? apiOptions : activeTemplateOptions(integrationSettings);
  }, [automationTemplates, integrationSettings]);
  const activeGroup = groups.find((group) => group.is_active) || groups[0];
  const pageMeta = metadata(activeGroup?.metadata_json);
  const pageTitle = textMeta(pageMeta.pageTitle, 'General Settings');
  const pageSubtitle = textMeta(pageMeta.pageSubtitle, 'Manage app-level workflow behavior, automation rules, reminders and operational defaults.');

  const reloadAutomationLists = async () => {
    const [nextSources, nextLookerSources, nextMediaAssets, nextTemplates, nextOfferCampaigns] = await Promise.all([
      getAutomationDataSources().catch(() => [] as AutomationDataSourceSummary[]),
      listLookerSources().catch(() => [] as ConfiguredLookerSourceSummary[]),
      getCommunicationMediaAssets().catch(() => [] as CommunicationMediaAssetSummary[]),
      listAutomationTemplates().catch(() => [] as AutomationTemplateSummary[]),
      listOfferCampaigns().catch(() => [] as OfferCampaignAdminSummary[]),
    ]);
    setSources(nextSources);
    setLookerSources(nextLookerSources);
    setMediaAssets(nextMediaAssets);
    setAutomationTemplates(nextTemplates);
    setOfferCampaigns(nextOfferCampaigns);
    setOfferAuditCampaignId((current) => {
      if (current && nextOfferCampaigns.some((campaign) => campaign.id === current)) return current;
      return nextOfferCampaigns.find((campaign) => campaign.status === 'ACTIVE')?.id || nextOfferCampaigns[0]?.id || '';
    });
  };

  const goToIntegrationSettings = () => {
    if (typeof window === 'undefined') return;
    window.location.hash = 'integration-settings';
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  };

  const runSourceAction = async (label: string, action: () => Promise<unknown>) => {
    try {
      const result = await action();
      setSourceTestResult(`${label}: ${JSON.stringify(result).slice(0, 320)}`);
      await reloadAutomationLists();
    } catch (error) {
      setSourceTestResult(`${label} failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const deleteSource = async (source: AutomationDataSourceSummary) => {
    if (!window.confirm(`Delete Looker data source "${source.name}" (${source.key})? Workflows using this source must be moved first.`)) return;
    await runSourceAction(`Delete ${source.key}`, () => deleteAutomationDataSource(source.key));
  };

  const confirmDeactivateWorkflow = async (workflow: AutomationWorkflow) => {
    if (!window.confirm(`Deactivate workflow "${workflow.name}"? Scheduled processing will stop until it is activated again.`)) return;
    await onDeactivateWorkflow(workflow.key);
  };

  const confirmDeleteWorkflow = async (workflow: AutomationWorkflow) => {
    if (!window.confirm(`Delete workflow "${workflow.name}" (${workflow.key})? This should only be used for retired or test workflows.`)) return;
    await onDeleteWorkflow(workflow.key);
  };

  const validateWorkflowCard = async (workflow: AutomationWorkflow) => {
    await runSourceAction(`Validate ${workflow.key}`, () => validateAutomationWorkflow(workflow.key));
  };

  const clearSettingsAuditFilters = () => {
    setAuditWorkflowFilter('');
    setAuditDateFrom('');
    setAuditDateTo('');
    setAuditChangedBy('');
    setAuditPage(0);
  };

  const exportSettingsAudit = () => downloadCsv('general-settings-audit.csv', settingsAuditCsv(filteredAudit));

  const clearNotificationFilters = () => {
    setNotificationPage(1);
    setNotificationFilters(defaultCommunicationAuditFilters);
  };

  const exportNotificationAudit = () => downloadCsv('communication-notification-audit.csv', notificationAuditCsv(notifications?.rows ?? []));

  const loadOfferAudit = async () => {
    if (!offerAuditCampaignId) return;
    const [auditRows, enrollmentRows] = await Promise.all([
      getOfferCampaignAudit(offerAuditCampaignId, offerAuditFilters),
      getOfferCampaignEnrollments(offerAuditCampaignId, { mrn: offerAuditFilters.mrn, offerCode: offerAuditFilters.offerCode }),
    ]);
    setOfferAuditRows(auditRows);
    setOfferEnrollmentRows(enrollmentRows);
  };

  const runOfferOverride = async () => {
    if (!offerOverrideDraft || !offerAuditCampaignId) return;
    setOfferAuditBusy('Admin Override');
    try {
      await overrideOfferCampaignEnrollment(offerAuditCampaignId, offerOverrideDraft);
      setOfferOverrideDraft(null);
      setSelectedOfferEnrollment(null);
      await loadOfferAudit();
    } finally {
      setOfferAuditBusy('');
    }
  };

  useEffect(() => {
    setAuditPage((current) => Math.min(current, auditPageCount - 1));
  }, [auditPageCount]);

  useEffect(() => {
    if (!active) return;
    void reloadAutomationLists();
  }, [active]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    getCenters()
      .then((items) => {
        if (!cancelled) setCenters([...new Set(items.map((item) => item.trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => {
        if (!cancelled) setCenters([]);
      });
    return () => { cancelled = true; };
  }, [active]);

  useEffect(() => {
    if (!active || workflows.length === 0) return;
    let cancelled = false;
    Promise.all(workflows.map((workflow) =>
      getAutomationWorkflowTrigger(workflow.key)
        .then((trigger) => [workflow.key, trigger] as const)
        .catch(() => [workflow.key, null] as const),
    )).then((entries) => {
      if (!cancelled) setWorkflowTriggers(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
  }, [active, workflows]);

  useEffect(() => {
    if (!active || auditTab !== 'notifications') return;
    let cancelled = false;
    setNotificationLoading(true);
    getCommunicationAudit({ page: notificationPage, limit: 10, ...notificationFilters })
      .then((result) => { if (!cancelled) setNotifications(result); })
      .catch(() => { if (!cancelled) setNotifications({ page: 1, limit: 10, total: 0, rows: [] }); })
      .finally(() => { if (!cancelled) setNotificationLoading(false); });
    return () => { cancelled = true; };
  }, [active, auditTab, notificationPage, notificationFilters, notificationReload]);

  useEffect(() => {
    if (!active || !offerAuditCampaignId || (auditTab !== 'campaign' && auditTab !== 'enrollments')) return;
    void loadOfferAudit();
  }, [active, auditTab, offerAuditCampaignId, offerAuditFilters]);

  return (
    <section id="tab-general-settings" className="tab-content enterprise-compact enterprise-page" style={{ display: active ? 'block' : 'none' }}>
      <div className="general-settings-toolbar">
        <div>
          <p className="enterprise-eyebrow">{pageTitle}</p>
          <h3>{pageSubtitle}</h3>
        </div>
        <div>
          <button className="btn btn-outline compact-btn" disabled={!canUpdate} onClick={() => setEditing({ type: 'group' })}>Add Group</button>
          <button className="btn btn-primary compact-btn" disabled={!canUpdate || groups.length === 0} onClick={() => setEditing({ type: 'new-workflow' })}>Add Workflow</button>
        </div>
      </div>

      <div className="general-settings-groups">
        {groups.map((group) => (
          <div key={group.key} className="general-settings-group-card">
            <div>
              <strong>{group.name}</strong>
              <span>{group.description || group.key}</span>
            </div>
            <em className={`status-chip ${group.is_active ? 'status-good' : 'status-muted'}`}>{group.is_active ? 'Active' : 'Inactive'}</em>
            <div className="general-settings-card-actions">
              <button className="mini-btn" disabled={!canUpdate} onClick={() => setEditing({ type: 'group', group })}>Edit</button>
              <button className="mini-btn" disabled={!canUpdate} onClick={() => void onDeleteGroup(group.key)}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      <div className="card enterprise-table-card">
        <div className="enterprise-card-heading"><div><strong>Workflow Configuration</strong><span>{workflows.length} automation workflows plus configured offer campaign workflows</span></div></div>
        <div className="enterprise-table-wrap workflow-config-table-wrap">
          <table className="enterprise-table workflow-config-table">
            <thead><tr><th>Workflow</th><th>Looker Source</th><th>Trigger Field</th><th>Trigger Type</th><th>Messages</th><th>Status</th><th>Last Run</th><th>Updated By</th><th>Actions</th></tr></thead>
            <tbody>
              {workflows.map((workflow) => {
                const trigger = workflowTriggers[workflow.key];
                const source = sources.find((item) => item.key === trigger?.sourceKey);
                const activeMessages = workflow.steps.filter((step) => step.is_active).length;
                const summary = workflowReadiness(workflow, trigger, source);
                return (
                  <tr key={workflow.key}>
                    <td><strong>{workflow.name}</strong><small>{workflow.description || workflow.key} • {workflow.category} • v{workflow.version}</small></td>
                    <td><strong>{source?.name || trigger?.sourceKey || '-'}</strong><small>{source?.externalSourceId ? `Look ${source.externalSourceId}` : groups.find((group) => group.key === workflow.group_key)?.name || workflow.group_key}</small></td>
                    <td>{trigger?.anchorField ? friendlyFieldLabel(trigger.anchorField) : '-'}</td>
                    <td>
                      {trigger?.triggerMode ? trigger.triggerMode.replaceAll('_', ' ') : '-'}
                      <div className="workflow-readiness-strip">
                        {summary.map((item) => <span key={item.label} className={item.ok ? 'ready' : 'missing'}>{item.label}</span>)}
                      </div>
                    </td>
                    <td><strong>{activeMessages}</strong><small>{workflow.steps.length} total</small></td>
                    <td><span className={`status-chip ${workflow.is_enabled ? 'status-good' : 'status-muted'}`}>{workflow.is_enabled ? 'Enabled' : 'Disabled'}</span></td>
                    <td>{source?.lastSuccessAt ? new Date(source.lastSuccessAt).toLocaleString() : '-'}</td>
                    <td><strong>{workflow.updated_by || '-'}</strong><small>{workflow.updated_at ? new Date(workflow.updated_at).toLocaleString() : '-'}</small></td>
                    <td><div className="row-actions">
                      <button className="mini-btn pink" onClick={() => setEditing({ type: 'workflow', workflow })}>{canUpdate ? 'Edit' : 'View'}</button>
                      <button className="mini-btn" disabled={!canUpdate} onClick={() => void copyWorkflowFromRegistry(workflow, onCopyWorkflow)}>Copy</button>
                      <button className="mini-btn" disabled={!canUpdate} onClick={() => void validateWorkflowCard(workflow)}>Validate</button>
                      <button className="mini-btn green" disabled={!canUpdate || workflow.is_enabled} onClick={() => void onActivateWorkflow(workflow.key)}>Activate</button>
                      <button className="mini-btn" disabled={!canUpdate || !workflow.is_enabled} onClick={() => void confirmDeactivateWorkflow(workflow)}>Deactivate</button>
                      <button className="mini-btn" disabled={!canUpdate} onClick={() => void confirmDeleteWorkflow(workflow)}>Delete</button>
                      <button className="mini-btn" onClick={() => { setAuditWorkflowFilter(workflow.key); setAuditTab('settings'); setAuditPage(0); }}>View Audit</button>
                    </div></td>
                  </tr>
                );
              })}
            <OfferCampaignWorkflowSettings
              active={active}
              canUpdate={canUpdate}
                centers={centers}
                notify={(message, type, title) => {
                  if (onNotify) onNotify(message, type, title);
                  else setSourceTestResult(message);
                }}
                refreshSignal={offerCampaignRefresh}
                onViewAudit={(campaignKey) => {
                  setAuditWorkflowFilter(campaignKey);
                  setAuditTab('settings');
                  setAuditPage(0);
                  document.getElementById('general-settings-audit-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              />
              {!workflows.length && <tr><td colSpan={9} className="empty-cell">No workflows configured.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card enterprise-table-card">
        <div className="enterprise-card-heading">
          <div>
            <strong>Integration Looker Looks</strong>
            <span>{lookerSources.length} available Looker reports from Integration Settings. These are raw Look IDs, such as patient registration or advance payment Looks, used for field preview and workflow source selection.</span>
          </div>
          <div className="row-actions">
            <button className="mini-btn" onClick={() => void reloadAutomationLists()}>Refresh Looks</button>
            <button className="mini-btn pink" onClick={goToIntegrationSettings}>Configure in Integration Settings</button>
          </div>
        </div>
        <div className="enterprise-table-wrap">
          <table className="enterprise-table">
            <thead><tr><th>Looker Source</th><th>Setting Key</th><th>Look ID</th><th>Status</th><th>Fields</th><th>Action</th></tr></thead>
            <tbody>
              {lookerSources.map((source) => {
                const test = lookerSourceTests[source.key];
                return (
                  <tr key={source.key}>
                    <td><strong>{source.name}</strong><small>{source.description || 'Configured in Integration Settings'}</small></td>
                    <td>{source.key}</td>
                    <td>{source.lookId || '-'}</td>
                    <td><span className={`status-chip ${source.active && source.configured ? 'status-good' : 'status-muted'}`}>{source.active && source.configured ? 'Ready' : 'Needs setup'}</span></td>
                    <td>{test ? `${test.fields.length} fields, ${test.rowCount} sample rows` : '-'}</td>
                    <td><div className="row-actions">
                      <button className="mini-btn" disabled={testingLookerSource === source.key || !source.configured} onClick={() => void testConfiguredLookerSource(source.key, testLookerSource, setTestingLookerSource, setLookerSourceTests)}>{testingLookerSource === source.key ? 'Testing...' : 'Preview Fields'}</button>
                      <button className="mini-btn" disabled={!source.configured || !canUpdate} onClick={() => setEditing({ type: 'data-source', lookerSource: source })}>Create Source</button>
                    </div></td>
                  </tr>
                );
              })}
              {!lookerSources.length && <tr><td colSpan={6} className="empty-cell">No Looker Look ID settings found in Integration Settings. Use Configure in Integration Settings to add Look IDs.</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="general-settings-help general-settings-card-note">Use this section to answer: which Looker reports are available to the system?</p>
        <LookerFieldCatalog tests={lookerSourceTests} />
      </div>

      <div className="card enterprise-table-card">
        <div className="enterprise-card-heading">
          <div>
            <strong>Looker Data Sources</strong>
            <span>{sources.length} configured workflow feeds. These connect an available Looker Look to runtime automation rules, polling status, event type and workflow triggers.</span>
          </div>
          <div className="row-actions">
            <button className="mini-btn" onClick={() => void reloadAutomationLists()}>Refresh Sources</button>
            <button className="mini-btn pink" disabled={!canUpdate} onClick={() => setEditing({ type: 'data-source' })}>Add Source</button>
          </div>
        </div>
        <div className="enterprise-table-wrap">
          <table className="enterprise-table">
            <thead><tr><th>Source</th><th>Mode</th><th>Event</th><th>Status</th><th>Last Success</th><th>Last Failure</th><th>Action</th></tr></thead>
            <tbody>
              {sources.map((source) => (
                <tr key={source.key}>
                  <td><strong>{source.name}</strong><small>{source.key}</small></td>
                  <td>{source.sourceMode}</td>
                  <td>{source.eventType}</td>
                  <td><span className={`status-chip ${source.isActive ? 'status-good' : 'status-muted'}`}>{source.status}</span></td>
                  <td>{source.lastSuccessAt ? new Date(source.lastSuccessAt).toLocaleString() : '-'}</td>
                  <td>{source.lastFailureReason || '-'}</td>
                  <td><div className="row-actions">
                    <button className="mini-btn" disabled={!canUpdate} onClick={() => setEditing({ type: 'data-source', source })}>Edit</button>
                    <button className="mini-btn" disabled={!canUpdate} onClick={() => void runSourceAction(`Test ${source.key}`, () => testAutomationDataSource(source.key))}>Test</button>
                    <button className="mini-btn" disabled={!canUpdate || source.isActive} onClick={() => void runSourceAction(`Activate ${source.key}`, () => activateAutomationDataSource(source.key))}>Activate</button>
                    <button className="mini-btn" disabled={!canUpdate || !source.isActive} onClick={() => void runSourceAction(`Deactivate ${source.key}`, () => deactivateAutomationDataSource(source.key))}>Deactivate</button>
                    <button className="mini-btn" disabled={!canUpdate} onClick={() => void deleteSource(source)}>Delete</button>
                  </div></td>
                </tr>
              ))}
              {!sources.length && <tr><td colSpan={7} className="empty-cell">No Looker data sources configured.</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="general-settings-help general-settings-card-note">Use this section to answer: how does a workflow consume a configured Looker report?</p>
        {sourceTestResult && <p className="general-settings-help" style={{ padding: '0 18px 18px' }}>{sourceTestResult}</p>}
      </div>

      <div className="card enterprise-table-card">
        <div className="enterprise-card-heading">
          <div><strong>Communication Media Assets</strong><span>{mediaAssets.length} approved media records</span></div>
          <div className="row-actions">
            <button className="mini-btn" onClick={() => void reloadAutomationLists()}>Refresh Media</button>
            <button className="mini-btn pink" disabled={!canUpdate} onClick={() => setEditing({ type: 'media-asset' })}>Add Media</button>
          </div>
        </div>
        <div className="enterprise-table-wrap">
          <table className="enterprise-table">
            <thead><tr><th>Asset</th><th>Type</th><th>Source</th><th>MIME</th><th>Size</th><th>Scope</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {mediaAssets.map((asset) => (
                <tr key={`${asset.key}-${asset.version}`}>
                  <td><strong>{asset.name}</strong><small>{asset.key} v{asset.version}</small></td>
                  <td>{asset.mediaType}</td>
                  <td>{asset.sourceType}</td>
                  <td>{asset.mimeType || '-'}</td>
                  <td>{asset.fileSizeBytes ? `${Math.round(Number(asset.fileSizeBytes) / 1024)} KB` : '-'}</td>
                  <td>{asset.scopeType}{asset.scopeValue ? `: ${asset.scopeValue}` : ''}</td>
                  <td><span className={`status-chip ${asset.isActive && asset.validationStatus === 'VALID' ? 'status-good' : 'status-muted'}`}>{asset.validationStatus}</span></td>
                  <td><div className="row-actions">
                    <button className="mini-btn" onClick={() => setEditing({ type: 'media-preview', asset, usages: mediaAssetUsages(workflows, asset) })}>Preview</button>
                    <button className="mini-btn" disabled={!canUpdate} onClick={() => setEditing({ type: 'media-asset', asset })}>Edit</button>
                    <button className="mini-btn" disabled={!canUpdate} onClick={() => void validateCommunicationMediaAsset(asset.key, asset.version).then(reloadAutomationLists)}>Validate</button>
                    <button className="mini-btn" disabled={!canUpdate || asset.validationStatus !== 'VALID'} onClick={() => void activateCommunicationMediaAsset(asset.key, asset.version).then(reloadAutomationLists)}>Activate</button>
                    <button className="mini-btn" disabled={!canUpdate || !asset.isActive} onClick={() => void deactivateCommunicationMediaAsset(asset.key, asset.version).then(reloadAutomationLists)}>Deactivate</button>
                  </div></td>
                </tr>
              ))}
              {!mediaAssets.length && <tr><td colSpan={8} className="empty-cell">No media assets configured.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div id="general-settings-audit-card" className="card enterprise-table-card">
        <div className="enterprise-card-heading">
          <div><strong>Audit</strong><span>{auditSubtitle(auditTab)}</span></div>
          {auditTab === 'settings' && <div className="general-settings-audit-pager" aria-label="General Settings audit pagination">
            <button type="button" className="mini-btn" onClick={exportSettingsAudit}>Export</button>
            <button type="button" className="mini-btn" onClick={clearSettingsAuditFilters}>Clear Filters</button>
            <button
              type="button"
              className="mini-btn"
              aria-label="Previous audit page"
              disabled={auditPage === 0}
              onClick={() => setAuditPage((current) => Math.max(0, current - 1))}
            >
              &lsaquo;
            </button>
            <span>{filteredAudit.length ? `${auditPage + 1} / ${auditPageCount}` : '0 / 0'}</span>
            <button
              type="button"
              className="mini-btn"
              aria-label="Next audit page"
              disabled={auditPage >= auditPageCount - 1}
              onClick={() => setAuditPage((current) => Math.min(auditPageCount - 1, current + 1))}
            >
              &rsaquo;
            </button>
          </div>}
          {auditTab === 'notifications' && <div className="general-settings-audit-pager" aria-label="Communication notification audit actions">
            <button type="button" className="mini-btn" onClick={() => setNotificationReload((value) => value + 1)}>Refresh</button>
            <button type="button" className="mini-btn" onClick={exportNotificationAudit}>Export</button>
            <button type="button" className="mini-btn" onClick={clearNotificationFilters}>Clear Filters</button>
            <NotificationAuditPager data={notifications} page={notificationPage} onPage={setNotificationPage} />
          </div>}
        </div>
        <div className="general-settings-audit-tabs-row">
          <div className="general-settings-yes-no" aria-label="General Settings audit tabs">
            <button type="button" className={auditTab === 'settings' ? 'active' : ''} onClick={() => setAuditTab('settings')}>Settings</button>
            <button type="button" className={auditTab === 'notifications' ? 'active' : ''} onClick={() => setAuditTab('notifications')}>Communication Notifications</button>
            <button type="button" className={auditTab === 'campaign' ? 'active' : ''} onClick={() => setAuditTab('campaign')}>Campaign Audit</button>
            <button type="button" className={auditTab === 'enrollments' ? 'active' : ''} onClick={() => setAuditTab('enrollments')}>Enrollment Drawer</button>
          </div>
        </div>
        {auditTab === 'settings' ? (
        <>
          <div className="general-settings-control-grid general-settings-audit-filters">
            <label style={labelStyle}>From<input type="date" value={auditDateFrom} onChange={(event) => { setAuditDateFrom(event.target.value); setAuditPage(0); }} style={inputStyle} /></label>
            <label style={labelStyle}>To<input type="date" value={auditDateTo} onChange={(event) => { setAuditDateTo(event.target.value); setAuditPage(0); }} style={inputStyle} /></label>
            <label style={labelStyle}>Workflow<select value={auditWorkflowFilter} onChange={(event) => { setAuditWorkflowFilter(event.target.value); setAuditPage(0); }} style={inputStyle}><option value="">All Workflows</option>{workflows.map((workflow) => <option key={workflow.key} value={workflow.key}>{workflow.name}</option>)}</select></label>
            <label style={labelStyle}>Changed By<input value={auditChangedBy} onChange={(event) => { setAuditChangedBy(event.target.value); setAuditPage(0); }} placeholder="User name..." style={inputStyle} /></label>
          </div>
          <div className="enterprise-table-wrap">
            <table className="enterprise-table">
              <thead><tr><th>When</th><th>Target</th><th>Action</th><th>Changed By</th></tr></thead>
              <tbody>
                {visibleAudit.map((entry) => <tr key={entry.id}><td>{new Date(entry.createdAt).toLocaleString()}</td><td>{entry.workflowKey}</td><td>{entry.action.replaceAll('_', ' ')}</td><td>{entry.changedBy || '-'}</td></tr>)}
                {!filteredAudit.length && <tr><td colSpan={4} className="empty-cell">No audit entries match the selected filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
        ) : auditTab === 'notifications' ? (
          <NotificationAuditTable
            data={notifications}
            loading={notificationLoading}
            page={notificationPage}
            filters={notificationFilters}
            workflows={workflows}
            sources={sources}
            timeline={notificationTimeline}
            timelineLoading={notificationTimelineLoading}
            canUpdate={canUpdate}
            onPage={setNotificationPage}
            onFilters={(filters) => { setNotificationPage(1); setNotificationFilters(filters); }}
            onViewTimeline={async (id) => {
              setNotificationTimelineLoading(true);
              try {
                setNotificationTimeline(await getCommunicationAuditTimeline(id));
              } finally {
                setNotificationTimelineLoading(false);
              }
            }}
            onCloseTimeline={() => setNotificationTimeline(null)}
            onRetry={async (id) => { await retryCommunicationMessage(id); setNotificationReload((value) => value + 1); }}
            onCancel={async (id) => { await cancelCommunicationMessage(id, 'Cancelled from General Settings communication audit.'); setNotificationReload((value) => value + 1); }}
          />
        ) : auditTab === 'campaign' ? (
          <OfferCampaignAuditPanel
            campaigns={offerCampaigns}
            selectedCampaignId={offerAuditCampaignId}
            filters={offerAuditFilters}
            auditRows={offerAuditRows}
            onCampaignChange={setOfferAuditCampaignId}
            onFiltersChange={setOfferAuditFilters}
            onRefresh={() => void loadOfferAudit()}
            onExport={() => exportOfferCampaignAuditCsv(offerAuditRows)}
            onSelectAudit={setSelectedOfferAuditRow}
          />
        ) : (
          <OfferCampaignEnrollmentDrawerPanel
            campaigns={offerCampaigns}
            selectedCampaignId={offerAuditCampaignId}
            filters={offerAuditFilters}
            enrollmentRows={offerEnrollmentRows}
            canOverride={canUpdate}
            onCampaignChange={setOfferAuditCampaignId}
            onFiltersChange={setOfferAuditFilters}
            onRefresh={() => void loadOfferAudit()}
            onExport={() => exportOfferCampaignEnrollmentCsv(offerEnrollmentRows)}
            onSelectEnrollment={setSelectedOfferEnrollment}
            onOverride={(row) => setOfferOverrideDraft({
              enrollmentId: row.id,
              mrn: row.mrn,
              offerCode: row.offerCode,
              overrideAction: 'MARK_VERIFIED',
              reason: '',
              confirm: false,
            })}
          />
        )}
      </div>

      {selectedOfferAuditRow && (
        <OfferCampaignAuditDetailDrawer row={selectedOfferAuditRow} onClose={() => setSelectedOfferAuditRow(null)} />
      )}
      {selectedOfferEnrollment && (
        <OfferCampaignEnrollmentDetailDrawer
          row={selectedOfferEnrollment}
          canOverride={canUpdate}
          onClose={() => setSelectedOfferEnrollment(null)}
          onOverride={(row) => setOfferOverrideDraft({
            enrollmentId: row.id,
            mrn: row.mrn,
            offerCode: row.offerCode,
            overrideAction: 'MARK_VERIFIED',
            reason: '',
            confirm: false,
          })}
        />
      )}
      {offerOverrideDraft && (
        <OfferCampaignOverrideEnrollmentModal
          draft={offerOverrideDraft}
          busy={offerAuditBusy === 'Admin Override'}
          onChange={setOfferOverrideDraft}
          onClose={() => setOfferOverrideDraft(null)}
          onSubmit={() => void runOfferOverride()}
        />
      )}

      {editing?.type === 'workflow' && (
        <WorkflowEditor
          workflow={editing.workflow}
          groups={groups}
          centers={centers}
          sources={sources}
          lookerSources={lookerSources}
          lookerSourceTests={lookerSourceTests}
          onPreviewLookerSource={(key) => testConfiguredLookerSource(key, testLookerSource, setTestingLookerSource, setLookerSourceTests)}
          onEditDataSource={(source, lookerSource) => setEditing({ type: 'data-source', source, lookerSource })}
          mediaAssets={mediaAssets}
          canUpdate={canUpdate}
          templateOptions={templateOptions}
          onRefreshAutomationLists={reloadAutomationLists}
          onClose={() => setEditing(null)}
          onSave={async (payload) => {
            await onSaveWorkflow(editing.workflow.key, payload);
            setEditing(null);
          }}
        />
      )}
      {editing?.type === 'new-workflow' && (
        <WorkflowCreateModal
          groups={groups}
          canUpdate={canUpdate}
          onClose={() => setEditing(null)}
          onCreate={async (payload) => { await onCreateWorkflow(payload); setEditing(null); }}
          onCreateOfferCampaign={async (payload) => {
            await onCreateOfferCampaign({
              campaignKey: payload.key,
              campaignName: payload.name,
              routeSlug: payload.key.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
              displayRouteSlug: `${payload.key.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}-display`,
              status: 'DRAFT',
              startsAt: null,
              endsAt: null,
              totalSlotLimit: 1,
              discountFormulaType: 'AGE_MULTIPLIER',
              ageMultiplier: 0,
              minAge: null,
              maxAge: null,
              eligibleCenters: [],
              codePattern: '{CAMPAIGN_KEY}-{CENTER_CODE}-{YYYYMMDD}-{SEQ}',
              codeSequenceScope: 'CENTER_DAY',
              reservationTtlSeconds: 900,
              displayRefreshSeconds: 10,
              celebrationEffect: 'SPARKLE_CONFETTI',
              themeConfig: {
                description: payload.description || '',
                reservationsConsumeSlots: true,
                configurationReview: {
                  totalSlotLimit: false,
                  codePattern: false,
                },
              },
              // Source configuration belongs to the campaign editor. Creating
              // the Draft shell must not manufacture blank Looker source rows.
              sources: [],
            });
            setOfferCampaignRefresh((value) => value + 1);
            setEditing(null);
          }}
        />
      )}
      {editing?.type === 'group' && (
        <GroupEditor group={editing.group} canUpdate={canUpdate} onClose={() => setEditing(null)} onSave={async (payload) => { await onSaveGroup(editing.group?.key || null, payload); setEditing(null); }} />
      )}
      {editing?.type === 'data-source' && (
        <DataSourceEditor
          source={editing.source}
          lookerSource={editing.lookerSource}
          lookerSources={lookerSources}
          lookerSourceTests={lookerSourceTests}
          canUpdate={canUpdate}
          onPreviewLookerSource={(key) => testConfiguredLookerSource(key, testLookerSource, setTestingLookerSource, setLookerSourceTests)}
          onClose={() => setEditing(null)}
          onSave={async (payload, activateAfterSave) => {
            const response = editing.source
              ? await updateAutomationDataSource(editing.source.key, payload)
              : await createAutomationDataSource(payload);
            if (activateAfterSave) await activateAutomationDataSource(response.source.key);
            await reloadAutomationLists();
            setSourceTestResult(`${activateAfterSave ? 'Saved and activated' : 'Saved'} ${response.source.key}`);
            setEditing(null);
          }}
        />
      )}
      {editing?.type === 'media-asset' && (
        <MediaAssetEditor
          asset={editing.asset}
          canUpdate={canUpdate}
          onClose={() => setEditing(null)}
          onSave={async (payload) => {
            if (editing.asset) await updateCommunicationMediaAsset(editing.asset.key, editing.asset.version, payload);
            else await createCommunicationMediaAsset(payload);
            await reloadAutomationLists();
            setEditing(null);
          }}
        />
      )}
      {editing?.type === 'media-preview' && (
        <MediaAssetPreview asset={editing.asset} usages={editing.usages} onClose={() => setEditing(null)} />
      )}
    </section>
  );
}

function NotificationAuditTable({
  data,
  loading,
  page,
  filters,
  workflows,
  sources,
  timeline,
  timelineLoading,
  canUpdate,
  onPage,
  onFilters,
  onViewTimeline,
  onCloseTimeline,
  onRetry,
  onCancel,
}: {
  data: CommunicationAuditResponse | null;
  loading: boolean;
  page: number;
  filters: CommunicationAuditFilters;
  workflows: AutomationWorkflow[];
  sources: AutomationDataSourceSummary[];
  timeline: CommunicationAuditTimeline | null;
  timelineLoading: boolean;
  canUpdate: boolean;
  onPage: (page: number) => void;
  onFilters: (filters: CommunicationAuditFilters) => void;
  onViewTimeline: (id: string) => Promise<void>;
  onCloseTimeline: () => void;
  onRetry: (id: string) => Promise<void>;
  onCancel: (id: string) => Promise<void>;
}) {
  const rows = data?.rows ?? [];
  const stepOptions = useMemo(() => {
    const steps = new Map<string, string>();
    workflows.forEach((workflow) => workflow.steps.forEach((step) => steps.set(step.step_key, step.step_name || step.step_key)));
    rows.forEach((row) => steps.set(row.stepKey, row.messageName || row.stepKey));
    return [...steps.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [workflows, rows]);
  const updateFilter = (key: keyof CommunicationAuditFilters, value: string) => onFilters({ ...filters, [key]: value });
  return (
    <div>
      <div className="general-settings-control-grid" style={{ padding: '0 18px 14px' }}>
        <label style={labelStyle}>From
          <input type="date" value={filters.dateFrom} onChange={(event) => updateFilter('dateFrom', event.target.value)} style={inputStyle} />
        </label>
        <label style={labelStyle}>To
          <input type="date" value={filters.dateTo} onChange={(event) => updateFilter('dateTo', event.target.value)} style={inputStyle} />
        </label>
        <label style={labelStyle}>Workflow
          <select value={filters.workflowKey} onChange={(event) => updateFilter('workflowKey', event.target.value)} style={inputStyle}>
            <option value="ALL">All Workflows</option>
            {workflows.map((workflow) => <option key={workflow.key} value={workflow.key}>{workflow.name}</option>)}
          </select>
        </label>
        <label style={labelStyle}>Message Step
          <select value={filters.stepKey} onChange={(event) => updateFilter('stepKey', event.target.value)} style={inputStyle}>
            <option value="ALL">All Steps</option>
            {stepOptions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <label style={labelStyle}>Looker Source
          <select value={filters.sourceKey} onChange={(event) => updateFilter('sourceKey', event.target.value)} style={inputStyle}>
            <option value="ALL">All Sources</option>
            {sources.map((source) => <option key={source.key} value={source.key}>{source.name}</option>)}
          </select>
        </label>
        <label style={labelStyle}>Center
          <input value={filters.center} onChange={(event) => updateFilter('center', event.target.value)} placeholder="Center name..." style={inputStyle} />
        </label>
        <label style={labelStyle}>Status
          <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)} style={inputStyle}>
            {['ALL', 'SCHEDULED', 'PROCESSING', 'SUBMITTED', 'SENT', 'DELIVERED', 'READ', 'UNKNOWN', 'FAILED', 'CANCELLED'].map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>
        <label style={labelStyle}>Delivered
          <select value={filters.delivered} onChange={(event) => updateFilter('delivered', event.target.value)} style={inputStyle}>
            {['ALL', 'YES', 'NO'].map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>
        <label style={labelStyle}>Read
          <select value={filters.read} onChange={(event) => updateFilter('read', event.target.value)} style={inputStyle}>
            {['ALL', 'YES', 'NO'].map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>
        <label style={labelStyle}>Subject / MRN / Source Key
          <input value={filters.search} onChange={(event) => updateFilter('search', event.target.value)} placeholder="Name, MRN, source key..." style={inputStyle} />
        </label>
      </div>
      <div className="enterprise-table-wrap">
        <table className="enterprise-table">
          <thead><tr><th>Center</th><th>Subject</th><th>Workflow</th><th>Message Step</th><th>Template</th><th>Scheduled At</th><th>Sent At</th><th>Status</th><th>Delivered</th><th>Read</th><th>Failure Reason</th><th>Actions</th></tr></thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>{row.center || '-'}</td>
                <td><strong>{row.patientFullName || row.subjectKey || '-'}</strong><small>{row.mrn || row.sourceEventKey || '-'}</small><small>{row.maskedRecipient || '-'}</small></td>
                <td><strong>{row.workflowName}</strong><small>{row.workflowKey}</small><small>{row.sourceName}</small></td>
                <td><strong>{row.messageName}</strong><small>{row.stepKey}</small></td>
                <td>{row.templateName || '-'}</td>
                <td>{formatDate(row.scheduledAt)}</td>
                <td>{formatDate(row.sentAt || row.submittedAt)}</td>
                <td><span className={`status-chip ${statusClass(row.status)}`}>{row.status}</span></td>
                <td><StatusMark done={row.delivered} at={row.deliveredAt} processing={['PROCESSING', 'SUBMITTED', 'SENT'].includes(row.status)} failed={row.status === 'FAILED'} /></td>
                <td><StatusMark done={row.read} at={row.readAt} processing={row.status === 'DELIVERED'} failed={row.status === 'FAILED'} /></td>
                <td>{row.failureReason || row.failureCode || '-'}</td>
                <td>
                  <div className="row-actions">
                    <button className="mini-btn" disabled={timelineLoading} onClick={() => void onViewTimeline(row.id)}>Timeline</button>
                    <button className="mini-btn" disabled={!canUpdate || row.status !== 'FAILED'} onClick={() => void onRetry(row.id)}>Retry</button>
                    <button className="mini-btn" disabled={!canUpdate || ['SUBMITTED', 'SENT', 'DELIVERED', 'READ', 'CANCELLED'].includes(row.status)} onClick={() => void onCancel(row.id)}>Cancel</button>
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={12} className="empty-cell">{loading ? 'Loading communication audit...' : 'No communication audit rows found.'}</td></tr>}
          </tbody>
        </table>
      </div>
      <NotificationAuditPager data={data} page={page} onPage={onPage} padded />
      {timeline && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="enterprise-modal compact-modal">
            <div className="enterprise-card-heading">
              <div><strong>Communication Timeline</strong><span>{timeline.patientFullName || timeline.mrn || timeline.id}</span></div>
              <button className="mini-btn" onClick={onCloseTimeline}>Close</button>
            </div>
            <div className="workflow-preview-panel">
              <p><strong>Status:</strong> {timeline.status}</p>
              <p><strong>Template:</strong> {timeline.templateName || '-'}</p>
              <p><strong>Recipient:</strong> {timeline.maskedRecipient || '-'}</p>
              <div className="enterprise-table-wrap">
                <table className="enterprise-table compact">
                  <thead><tr><th>Event</th><th>When</th><th>Status</th><th>Note</th></tr></thead>
                  <tbody>
                    {timeline.events.map((event) => <tr key={`${event.label}-${event.at}`}><td>{event.label}</td><td>{formatDate(event.at)}</td><td>{event.status}</td><td>{event.note || '-'}</td></tr>)}
                    {!timeline.events.length && <tr><td colSpan={4} className="empty-cell">No timeline events recorded yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationAuditPager({
  data,
  page,
  onPage,
  padded = false,
}: {
  data: CommunicationAuditResponse | null;
  page: number;
  onPage: (page: number) => void;
  padded?: boolean;
}) {
  const pageCount = notificationPageCount(data);
  return (
    <div className="general-settings-audit-pager" style={padded ? { padding: '14px 18px' } : undefined} aria-label="Communication notification audit pagination">
      <button className="mini-btn" disabled={page <= 1} onClick={() => onPage(Math.max(1, page - 1))}>&lsaquo;</button>
      <span>{data ? `${page} / ${pageCount} (${data.total})` : '0 / 0'}</span>
      <button className="mini-btn" disabled={page >= pageCount} onClick={() => onPage(Math.min(pageCount, page + 1))}>&rsaquo;</button>
    </div>
  );
}

function notificationPageCount(data: CommunicationAuditResponse | null) {
  return Math.max(1, Math.ceil((data?.total ?? 0) / (data?.limit ?? 10)));
}

function auditSubtitle(tab: 'settings' | 'notifications' | 'campaign' | 'enrollments') {
  if (tab === 'settings') return 'Latest General Settings changes';
  if (tab === 'notifications') return 'Communication notification message audit';
  if (tab === 'campaign') return 'Offer campaign event audit and override trail';
  return 'Offer campaign enrollment drawer and admin override source';
}

function startOfLocalDay(value: string) {
  return new Date(`${value}T00:00:00`);
}

function endOfLocalDay(value: string) {
  return new Date(`${value}T23:59:59.999`);
}

function downloadCsv(filename: string, csv: string) {
  if (typeof window === 'undefined') return;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

function csvCell(value: unknown) {
  const text = value === undefined || value === null ? '' : String(value);
  return `"${text.replaceAll('"', '""')}"`;
}

function settingsAuditCsv(rows: NonNullable<GeneralSettingsResponse['audit']>) {
  return [
    ['When', 'Target', 'Action', 'Changed By'].map(csvCell).join(','),
    ...rows.map((row) => [new Date(row.createdAt).toLocaleString(), row.workflowKey, row.action, row.changedBy || ''].map(csvCell).join(',')),
  ].join('\n');
}

function notificationAuditCsv(rows: CommunicationAuditRow[]) {
  return [
    ['Center', 'Subject', 'MRN', 'Workflow', 'Message Step', 'Template', 'Scheduled At', 'Sent At', 'Status', 'Delivered', 'Read', 'Failure Reason'].map(csvCell).join(','),
    ...rows.map((row) => [
      row.center || '',
      row.patientFullName || row.subjectKey || '',
      row.mrn || '',
      row.workflowName,
      row.messageName,
      row.templateName || '',
      formatDate(row.scheduledAt),
      formatDate(row.sentAt || row.submittedAt),
      row.status,
      row.delivered ? 'Yes' : 'No',
      row.read ? 'Yes' : 'No',
      row.failureReason || row.failureCode || '',
    ].map(csvCell).join(',')),
  ].join('\n');
}

function workflowReadiness(workflow: AutomationWorkflow, trigger: WorkflowTriggerResponse | null | undefined, source: AutomationDataSourceSummary | undefined) {
  const hasTemplate = workflow.steps.some((step) => step.is_active && Boolean(step.template_setting_key));
  const bindings = workflow.steps.reduce((count, step) => count + stepContract(step).action.bindings.length, 0);
  return [
    { label: source ? 'Source configured' : 'Source missing', ok: Boolean(source) },
    { label: trigger?.anchorField ? 'Trigger configured' : 'Trigger missing', ok: Boolean(trigger?.anchorField) },
    { label: hasTemplate ? 'Template selected' : 'Template missing', ok: hasTemplate },
    { label: bindings ? 'Bindings mapped' : 'Bindings pending', ok: bindings > 0 },
    { label: workflow.is_enabled ? 'Active' : 'Inactive', ok: workflow.is_enabled },
  ];
}

function mediaAssetUsages(workflows: AutomationWorkflow[], asset: CommunicationMediaAssetSummary) {
  const usages: string[] = [];
  workflows.forEach((workflow) => {
    workflow.steps.forEach((step) => {
      const matches = stepContract(step).action.bindings.filter((binding) => binding.mediaAssetKey === asset.key);
      matches.forEach((binding) => usages.push(`${workflow.name} / ${step.step_name} / ${templateBindingLabel(binding.address)}`));
    });
  });
  return usages;
}

async function testConfiguredLookerSource(
  key: string,
  request: typeof testLookerSource,
  setTesting: (key: string) => void,
  setResults: Dispatch<SetStateAction<Record<string, ConfiguredLookerSourceTestResponse>>>,
) {
  setTesting(key);
  try {
    const result = await request(key);
    setResults((current) => ({ ...current, [key]: result }));
  } finally {
    setTesting('');
  }
}

function orderedLookerSources(sources: ConfiguredLookerSourceSummary[]) {
  return [...sources].sort((a, b) => Number(b.active && b.configured) - Number(a.active && a.configured) || a.name.localeCompare(b.name));
}

function lookerSourceOptionLabel(source: ConfiguredLookerSourceSummary) {
  const status = source.active && source.configured ? 'Ready' : source.configured ? 'Inactive' : 'Missing Look ID';
  return `${source.name} - ${source.lookId || 'not configured'} [${status}]`;
}

function LookerFieldCatalog({ tests }: { tests: Record<string, ConfiguredLookerSourceTestResponse> }) {
  const entries = Object.entries(tests);
  if (!entries.length) return null;
  const [key, test] = entries[entries.length - 1];
  return (
    <div className="looker-field-catalog">
      <div className="enterprise-card-heading"><div><strong>Latest Field Preview</strong><span>{test.source.name} ({key})</span></div></div>
      <div className="enterprise-table-wrap">
        <table className="enterprise-table compact">
          <thead><tr><th>Field Label</th><th>Field Key</th><th>Type</th><th>Sample Value</th><th>IST Preview</th></tr></thead>
          <tbody>
            {test.fields.slice(0, 18).map((field) => (
              <tr key={field.fieldKey}>
                <td>{field.fieldLabel}</td>
                <td>{field.fieldKey}</td>
                <td>{field.type}</td>
                <td>{String(field.sampleValue ?? '-')}</td>
                <td>{field.istPreview || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {test.fields.length > 18 && <p className="general-settings-help" style={{ padding: '0 18px 14px' }}>Showing first 18 of {test.fields.length} fields.</p>}
    </div>
  );
}

function DataSourceEditor({
  source,
  lookerSource,
  lookerSources,
  lookerSourceTests,
  canUpdate,
  onPreviewLookerSource,
  onClose,
  onSave,
}: {
  source?: AutomationDataSourceSummary;
  lookerSource?: ConfiguredLookerSourceSummary;
  lookerSources: ConfiguredLookerSourceSummary[];
  lookerSourceTests: Record<string, ConfiguredLookerSourceTestResponse>;
  canUpdate: boolean;
  onPreviewLookerSource: (key: string) => Promise<void>;
  onClose: () => void;
  onSave: (payload: AutomationDataSourceContract, activateAfterSave: boolean) => Promise<void>;
}) {
  const initialLook = lookerSource || lookerSources.find((item) => String(item.lookId) === String(source?.externalSourceId));
  const [selectedLookerKey, setSelectedLookerKey] = useState(initialLook?.key || '');
  const selectedLooker = lookerSources.find((item) => item.key === selectedLookerKey);
  const preview = selectedLooker ? lookerSourceTests[selectedLooker.key] : undefined;
  const previewFields = preview?.fields.map((field) => field.fieldKey) ?? [];
  const [key, setKey] = useState(source?.key || sourceKeyFromLook(initialLook));
  const [name, setName] = useState(source?.name || initialLook?.name || '');
  const [description, setDescription] = useState(source?.description || initialLook?.description || '');
  const [sourceMode, setSourceMode] = useState<AutomationDataSourceContract['sourceMode']>((source?.sourceMode as AutomationDataSourceContract['sourceMode']) || 'SAVED_LOOK');
  const [externalSourceId, setExternalSourceId] = useState(source?.externalSourceId || String(initialLook?.lookId || ''));
  const [eventType, setEventType] = useState(source?.eventType || 'PATIENT_REGISTERED');
  const [pollIntervalSeconds, setPollIntervalSeconds] = useState(source?.pollIntervalSeconds || 300);
  const [overlapSeconds, setOverlapSeconds] = useState(source?.overlapSeconds || 300);
  const [queryModel, setQueryModel] = useState(source?.query?.model || 'ferty9');
  const [queryExplore, setQueryExplore] = useState(source?.query?.explore || 'patients');
  const [queryFieldsText, setQueryFieldsText] = useState((source?.query?.fields || []).join('\n'));
  const [queryFiltersText, setQueryFiltersText] = useState(recordToLines(source?.query?.filters));
  const [querySortsText, setQuerySortsText] = useState((source?.query?.sorts || []).join('\n'));
  const [queryLimit, setQueryLimit] = useState(source?.query?.limit || 500);
  const [fieldMappings, setFieldMappings] = useState<AutomationDataSourceContract['fieldMappings']>(() => ({
    eventKey: source?.fieldMappings?.eventKey || '',
    patientId: source?.fieldMappings?.patientId || '',
    mrn: source?.fieldMappings?.mrn || '',
    patientFullName: source?.fieldMappings?.patientFullName || '',
    center: source?.fieldMappings?.center || '',
    whatsappPhone: source?.fieldMappings?.whatsappPhone || '',
    registrationTimestamp: source?.fieldMappings?.registrationTimestamp || '',
    attributes: sanitizeAttributeMappings((source?.fieldMappings as AutomationDataSourceContract['fieldMappings'] | undefined)?.attributes),
  }));
  const [attributesText, setAttributesText] = useState(recordToLines(fieldMappings.attributes));
  const [activateAfterSave, setActivateAfterSave] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!selectedLooker) return;
    setExternalSourceId(String(selectedLooker.lookId || ''));
    if (!source) {
      setKey((current) => current || sourceKeyFromLook(selectedLooker));
      setName((current) => current || selectedLooker.name);
      setDescription((current) => current || selectedLooker.description || '');
    }
    if (selectedLooker.configured && !lookerSourceTests[selectedLooker.key]) void onPreviewLookerSource(selectedLooker.key);
  }, [selectedLookerKey]);

  useEffect(() => {
    if (queryFieldsText.trim() || !previewFields.length) return;
    setQueryFieldsText(previewFields.join('\n'));
  }, [previewFields.join('|')]);

  const updateMapping = (field: keyof Omit<AutomationDataSourceContract['fieldMappings'], 'attributes'>, value: string) => {
    setFieldMappings((current) => ({ ...current, [field]: value }));
  };

  const buildPayload = (): AutomationDataSourceContract => {
    const attributes = linesToRecord(attributesText);
    const queryFields = splitLines(queryFieldsText);
    const payload: AutomationDataSourceContract = {
      key: normalizeSourceKey(key),
      name: name.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      provider: 'LOOKER',
      sourceMode,
      ...(externalSourceId.trim() ? { externalSourceId: externalSourceId.trim() } : {}),
      eventType: eventType.trim().toUpperCase(),
      pollIntervalSeconds: clamp(Math.round(Number(pollIntervalSeconds) || 300), 30, 86400),
      overlapSeconds: clamp(Math.round(Number(overlapSeconds) || 0), 0, 2592000),
      fieldMappings: {
        eventKey: fieldMappings.eventKey,
        patientId: fieldMappings.patientId,
        mrn: fieldMappings.mrn,
        patientFullName: fieldMappings.patientFullName,
        center: fieldMappings.center,
        whatsappPhone: fieldMappings.whatsappPhone,
        registrationTimestamp: fieldMappings.registrationTimestamp,
        ...(Object.keys(attributes).length ? { attributes } : {}),
      },
      query: {
        model: queryModel.trim(),
        explore: queryExplore.trim(),
        fields: queryFields,
        ...(Object.keys(linesToRecord(queryFiltersText)).length ? { filters: linesToRecord(queryFiltersText) } : {}),
        ...(splitLines(querySortsText).length ? { sorts: splitLines(querySortsText) } : {}),
        limit: clamp(Math.round(Number(queryLimit) || 500), 1, 100000),
        timezone: 'Asia/Kolkata',
      },
    };
    return payload;
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = buildPayload();
      const missing = requiredMappingKeys.filter((field) => !payload.fieldMappings[field]);
      if (missing.length) throw new Error(`Missing required mappings: ${missing.join(', ')}`);
      if (!payload.query.fields.length) throw new Error('Query fields are required. Preview a Look or add qualified Looker fields.');
      await onSave(payload, activateAfterSave);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" style={modalOverlayStyle}>
      <div className="modal-content admin-premium-modal general-settings-modal" style={modalContentStyle} onKeyDown={handleModalScrollKeyDown} tabIndex={-1}>
        <button onClick={onClose} aria-label="Close data source editor" className="admin-modal-close">&times;</button>
        <div style={{ paddingRight: 34 }}>
          <p className="enterprise-eyebrow">Looker Data Source</p>
          <h3 style={modalTitleStyle}>{source ? `Edit ${source.name}` : 'Add Looker Data Source'}</h3>
          <p style={modalDescriptionStyle}>Connect an Integration Settings Look to the workflow runtime source contract.</p>
        </div>

        <div className="workflow-editor-tabs" role="tablist" aria-label="Data source editor sections">
          {['Source', 'Query', 'Field Mapping', 'Save'].map((label) => <span key={label}>{label}</span>)}
        </div>

        <div className="general-settings-section">
          <div className="enterprise-card-heading"><div><strong>Source Identity</strong><span>Choose the Integration Settings Look and runtime event type</span></div></div>
          <div className="general-settings-control-grid">
            <label style={labelStyle}>Integration Looker Look
              <select value={selectedLookerKey} disabled={!canUpdate || saving || Boolean(source)} onChange={(event) => setSelectedLookerKey(event.target.value)} style={inputStyle}>
                <option value="">Select configured Look</option>
                {orderedLookerSources(lookerSources).map((item) => <option key={item.key} value={item.key}>{lookerSourceOptionLabel(item)}</option>)}
              </select>
              <HelpText text="This list is read from Integration Settings. If it is empty, configure Look IDs there first." />
            </label>
            <label style={labelStyle}>Source Key<input value={key} disabled={!canUpdate || saving || Boolean(source)} onChange={(event) => setKey(event.target.value)} style={inputStyle} /></label>
            <label style={labelStyle}>Source Name<input value={name} disabled={!canUpdate || saving} onChange={(event) => setName(event.target.value)} style={inputStyle} /></label>
            <label style={labelStyle}>External Look ID<input value={externalSourceId} disabled={!canUpdate || saving} onChange={(event) => setExternalSourceId(event.target.value)} style={inputStyle} /></label>
            <label style={labelStyle}>Event Type<select value={eventType} disabled={!canUpdate || saving} onChange={(event) => setEventType(event.target.value)} style={inputStyle}>{eventTypeOptions.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
            <label style={labelStyle}>Source Mode<select value={sourceMode} disabled={!canUpdate || saving} onChange={(event) => setSourceMode(event.target.value as AutomationDataSourceContract['sourceMode'])} style={inputStyle}><option value="SAVED_LOOK">SAVED_LOOK</option><option value="REUSABLE_QUERY">REUSABLE_QUERY</option></select></label>
            <label style={labelStyle}>Poll Interval Seconds<input type="number" min={30} max={86400} value={pollIntervalSeconds} disabled={!canUpdate || saving} onChange={(event) => setPollIntervalSeconds(Number(event.target.value))} style={inputStyle} /></label>
            <label style={labelStyle}>Overlap Seconds<input type="number" min={0} max={2592000} value={overlapSeconds} disabled={!canUpdate || saving} onChange={(event) => setOverlapSeconds(Number(event.target.value))} style={inputStyle} /></label>
            <label style={labelStyle} className="user-modal-wide">Description<textarea value={description} disabled={!canUpdate || saving} onChange={(event) => setDescription(event.target.value)} rows={3} style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} /></label>
          </div>
        </div>

        <div className="general-settings-section">
          <div className="enterprise-card-heading">
            <div><strong>Looker Query</strong><span>Qualified fields, filters and query details used by the runtime poller</span></div>
            {selectedLooker && <button className="mini-btn" disabled={!selectedLooker.configured || saving} onClick={() => void onPreviewLookerSource(selectedLooker.key)}>Preview Fields</button>}
          </div>
          <div className="general-settings-control-grid">
            <label style={labelStyle}>Model<input value={queryModel} disabled={!canUpdate || saving} onChange={(event) => setQueryModel(event.target.value)} style={inputStyle} /></label>
            <label style={labelStyle}>Explore<input value={queryExplore} disabled={!canUpdate || saving} onChange={(event) => setQueryExplore(event.target.value)} style={inputStyle} /></label>
            <label style={labelStyle}>Limit<input type="number" min={1} max={100000} value={queryLimit} disabled={!canUpdate || saving} onChange={(event) => setQueryLimit(Number(event.target.value))} style={inputStyle} /></label>
            <label style={labelStyle} className="user-modal-wide">Query Fields<textarea value={queryFieldsText} disabled={!canUpdate || saving} onChange={(event) => setQueryFieldsText(event.target.value)} rows={6} placeholder="patients.import_timestamp_time" style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} /><HelpText text="One qualified Looker field per line. Previewing a Look can populate this from the sample response." /></label>
            <label style={labelStyle} className="user-modal-wide">Filters<textarea value={queryFiltersText} disabled={!canUpdate || saving} onChange={(event) => setQueryFiltersText(event.target.value)} rows={3} placeholder="patients.center=KPHB" style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} /><HelpText text="Optional. Use one key=value filter per line." /></label>
            <label style={labelStyle} className="user-modal-wide">Sorts<textarea value={querySortsText} disabled={!canUpdate || saving} onChange={(event) => setQuerySortsText(event.target.value)} rows={3} placeholder="patients.import_timestamp_time desc" style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} /></label>
          </div>
          {preview && <InlineFieldChips fields={preview.fields} />}
        </div>

        <div className="general-settings-section">
          <div className="enterprise-card-heading"><div><strong>Required Field Mapping</strong><span>Map workflow patient fields to qualified Looker fields</span></div></div>
          <div className="general-settings-control-grid">
            {requiredMappingKeys.map((mappingKey) => (
              <LookerFieldSelector
                key={mappingKey}
                label={mappingLabel(mappingKey)}
                value={fieldMappings[mappingKey]}
                fields={previewFields}
                disabled={!canUpdate || saving}
                onChange={(value) => updateMapping(mappingKey, value)}
              />
            ))}
            <label style={labelStyle} className="user-modal-wide">Attribute Mappings<textarea value={attributesText} disabled={!canUpdate || saving} onChange={(event) => setAttributesText(event.target.value)} rows={4} placeholder="doctorName=appointments.doctor_name" style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} /><HelpText text="Optional. One normalizedName=looker.field mapping per line for template variables and conditions." /></label>
          </div>
        </div>

        {error && <div className="admin-error-banner">{error}</div>}
        <div style={modalActionsStyle}>
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          {source && <button className="btn btn-outline" disabled={saving} onClick={() => void onPreviewLookerSource(selectedLookerKey)}>Preview Look</button>}
          <label className="general-settings-inline-toggle"><input type="checkbox" checked={activateAfterSave} disabled={!canUpdate || saving} onChange={(event) => setActivateAfterSave(event.target.checked)} /> Save and activate</label>
          <button className="btn btn-primary" disabled={!canUpdate || saving} onClick={() => void save()}>{saving ? 'Saving...' : 'Save Source'}</button>
        </div>
      </div>
    </div>
  );
}

function LookerFieldSelector({
  label,
  value,
  fields,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  fields: string[];
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <label style={labelStyle}>{label}
      <input list={`looker-field-${label.replace(/\W+/g, '-').toLowerCase()}`} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} placeholder="qualified.looker_field" style={inputStyle} />
      <datalist id={`looker-field-${label.replace(/\W+/g, '-').toLowerCase()}`}>
        {fields.map((field) => <option key={field} value={field} />)}
      </datalist>
    </label>
  );
}

const eventTypeOptions = ['PATIENT_REGISTERED', 'APPOINTMENT_EVENT', 'FEEDBACK_ELIGIBLE', 'MEDICATION_EVENT', 'CUSTOM_EVENT'];
const requiredMappingKeys: Array<keyof Omit<AutomationDataSourceContract['fieldMappings'], 'attributes'>> = [
  'eventKey',
  'patientId',
  'mrn',
  'patientFullName',
  'center',
  'whatsappPhone',
  'registrationTimestamp',
];

function mappingLabel(key: keyof Omit<AutomationDataSourceContract['fieldMappings'], 'attributes'>) {
  const labels: Record<keyof Omit<AutomationDataSourceContract['fieldMappings'], 'attributes'>, string> = {
    eventKey: 'Event Key',
    patientId: 'Patient ID',
    mrn: 'MRN',
    patientFullName: 'Patient Full Name',
    center: 'Center',
    whatsappPhone: 'WhatsApp Phone',
    registrationTimestamp: 'Registration / Trigger Timestamp',
  };
  return labels[key];
}

function sourceKeyFromLook(source?: ConfiguredLookerSourceSummary) {
  if (!source) return '';
  return normalizeSourceKey(source.key.replace(/^LOOKER_/, '').replace(/_LOOK_ID$/, ''));
}

function normalizeSourceKey(value: string) {
  const normalized = value.trim().replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
  return /^[A-Za-z]/.test(normalized) ? normalized : `SOURCE_${normalized || 'NEW'}`;
}

function splitLines(value: string) {
  return value.split(/\r?\n|,/).map((item) => item.trim()).filter(Boolean);
}

function recordToLines(value?: Record<string, string>) {
  return Object.entries(value || {}).map(([key, item]) => `${key}=${item}`).join('\n');
}

function linesToRecord(value: string) {
  return splitLines(value).reduce<Record<string, string>>((record, line) => {
    const index = line.indexOf('=');
    if (index <= 0) return record;
    const key = line.slice(0, index).trim();
    const item = line.slice(index + 1).trim();
    if (key && item) record[key] = item;
    return record;
  }, {});
}

function sanitizeAttributeMappings(value?: Record<string, string>) {
  return Object.fromEntries(Object.entries(value || {}).filter(([key, item]) => key && item));
}

async function copyWorkflowFromRegistry(
  workflow: AutomationWorkflow,
  onCopyWorkflow: (key: string, payload: { key?: string; name?: string; description?: string }) => Promise<void>,
) {
  const suggestedKey = `${workflow.key}_COPY`;
  const key = window.prompt('New workflow key', suggestedKey);
  if (!key) return;
  const name = window.prompt('New workflow name', `${workflow.name} Copy`);
  if (!name) return;
  await onCopyWorkflow(workflow.key, { key, name, description: workflow.description || undefined });
}

function StatusMark({ done, at, processing, failed }: { done: boolean; at?: string | null; processing?: boolean; failed?: boolean }) {
  if (done) return <span title={formatDate(at || '')} style={{ color: '#059669', fontWeight: 900 }}>✓</span>;
  if (failed) return <span title="Failed" style={{ color: '#dc2626', fontWeight: 900 }}>×</span>;
  if (processing) return <span title="Waiting for provider confirmation" style={{ color: '#64748b' }}>◌</span>;
  return <span title="Not confirmed yet" style={{ color: '#94a3b8' }}>—</span>;
}

function statusClass(value: string) {
  if (['DELIVERED', 'READ', 'SENT', 'SUBMITTED'].includes(value)) return 'status-good';
  if (value === 'FAILED') return 'status-bad';
  return 'status-muted';
}

function formatDate(value?: string | Date | null) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString() : '-';
}

function handleModalScrollKeyDown(event: KeyboardEvent<HTMLElement>) {
  if (shouldKeepKeyInsideFocusedControl(event.target)) return;
  const container = event.currentTarget;
  if (container.scrollHeight <= container.clientHeight) return;
  const pageStep = Math.max(120, Math.floor(container.clientHeight * 0.82));
  let nextTop: number | null = null;
  let delta = 0;
  if (event.key === 'ArrowDown') delta = 52;
  else if (event.key === 'ArrowUp') delta = -52;
  else if (event.key === 'PageDown') delta = pageStep;
  else if (event.key === 'PageUp') delta = -pageStep;
  else if (event.key === 'Home') nextTop = 0;
  else if (event.key === 'End') nextTop = container.scrollHeight;
  else return;
  event.preventDefault();
  event.stopPropagation();
  if (nextTop !== null) container.scrollTo({ top: nextTop, behavior: 'auto' });
  else container.scrollBy({ top: delta, behavior: 'auto' });
}

function shouldKeepKeyInsideFocusedControl(target: EventTarget | null) {
  if (!(target instanceof Element)) return false;
  return Boolean(target.closest('input, textarea, select, [contenteditable="true"], [role="combobox"], [role="listbox"]'));
}

function WorkflowEditor({
  workflow,
  groups,
  centers,
  sources,
  lookerSources,
  lookerSourceTests,
  onPreviewLookerSource,
  onEditDataSource,
  mediaAssets,
  canUpdate,
  templateOptions,
  onRefreshAutomationLists,
  onClose,
  onSave,
}: {
  workflow: AutomationWorkflow;
  groups: GeneralSettingsGroup[];
  centers: string[];
  sources: AutomationDataSourceSummary[];
  lookerSources: ConfiguredLookerSourceSummary[];
  lookerSourceTests: Record<string, ConfiguredLookerSourceTestResponse>;
  onPreviewLookerSource: (key: string) => Promise<void>;
  onEditDataSource: (source?: AutomationDataSourceSummary, lookerSource?: ConfiguredLookerSourceSummary) => void;
  mediaAssets: CommunicationMediaAssetSummary[];
  canUpdate: boolean;
  templateOptions: TemplateOption[];
  onRefreshAutomationLists: () => Promise<void>;
  onClose: () => void;
  onSave: (payload: AutomationWorkflowPayload) => Promise<void>;
}) {
  const modalScrollRef = useRef<HTMLDivElement | null>(null);
  const [name, setName] = useState(workflow.name);
  const [description, setDescription] = useState(workflow.description || '');
  const [groupKey, setGroupKey] = useState(workflow.group_key);
  const [category, setCategory] = useState(workflow.category);
  const [scopeType, setScopeType] = useState(workflow.scope_type || 'GLOBAL');
  const [scopeValue, setScopeValue] = useState(workflow.scope_value || '');
  const [enabled, setEnabled] = useState(workflow.is_enabled);
  const [manualOverride, setManualOverride] = useState(workflow.manual_policy === 'ALLOW_MANUAL_OVERRIDE');
  const [settings, setSettings] = useState(workflow.settings);
  const [steps, setSteps] = useState(() => workflow.steps.map(ensureStepContract));
  const [trigger, setTrigger] = useState<AutomationTriggerContract>(() => defaultTrigger(sources));
  const [sampleTimestamp, setSampleTimestamp] = useState(() => new Date().toISOString().slice(0, 16));
  const [operationResult, setOperationResult] = useState('');
  const [validation, setValidation] = useState<WorkflowValidationResponse | null>(null);
  const [pendingImpact, setPendingImpact] = useState<WorkflowPendingImpactResponse | null>(null);
  const [schedulePreview, setSchedulePreview] = useState<WorkflowSchedulePreviewResponse | null>(null);
  const [matchingRowsPreview, setMatchingRowsPreview] = useState<unknown>(null);
  const [templatePreview, setTemplatePreview] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [activePanel, setActivePanel] = useState<WorkflowEditorPanel>('basic');
  const [messageDetailsOpen, setMessageDetailsOpen] = useState(true);
  const reminderCountSetting = settings.find((setting) => setting.setting_key === 'REMINDER_COUNT');
  const reminderCount = Number(readSettingValue(settings, 'REMINDER_COUNT') || steps.length || 1);
  const sections = groupedSettings(settings);
  const preview = useMemo(() => calculatePreview(settings, steps), [settings, steps]);
  const selectedSource = sources.find((source) => source.key === trigger.sourceKey);
  const selectedLookerSource = selectedSource ? lookerSources.find((source) => String(source.lookId) === String(selectedSource.externalSourceId)) : undefined;
  const selectedLookerTest = selectedLookerSource ? lookerSourceTests[selectedLookerSource.key] : undefined;
  const triggerFieldOptions = useMemo(() => fieldOptions(selectedSource, trigger, selectedLookerTest?.fields), [selectedSource, trigger, selectedLookerTest]);
  const isPatientCommunication = groupKey === 'PATIENT_COMMUNICATION';
  const activePanelIndex = workflowEditorPanels.findIndex((panel) => panel.key === activePanel);

  useEffect(() => {
    const timer = window.setTimeout(() => modalScrollRef.current?.focus({ preventScroll: true }), 0);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getAutomationWorkflowTrigger(workflow.key)
      .then((existing) => {
        if (cancelled) return;
        setTrigger(existing ? normalizeTriggerResponse(existing) : defaultTrigger(sources));
      })
      .catch((error) => {
        if (!cancelled) setOperationResult(`Trigger load failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      });
    return () => { cancelled = true; };
  }, [workflow.key, sources]);

  useEffect(() => {
    if (!reminderCountSetting) return;
    setSteps((current) => normalizeStepCount(current, reminderCount, controlMeta(reminderCountSetting)).map(ensureStepContract));
  }, [reminderCount, reminderCountSetting]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        name,
        description,
        groupKey,
        category,
        scopeType,
        scopeValue: scopeType === 'CENTER' ? scopeValue : undefined,
        isEnabled: enabled,
        manualPolicy: isPatientCommunication || !manualOverride ? 'STRICT_SCHEDULE_ONLY' : 'ALLOW_MANUAL_OVERRIDE',
        settings: settings.map((setting) => ({ settingKey: setting.setting_key, settingValue: setting.setting_value_json })),
        steps: steps.map(toStepPayload),
      });
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (label: string, action: () => Promise<unknown>) => {
    setSaving(true);
    try {
      const result = await action();
      setOperationResult(`${label}: ${JSON.stringify(result, null, 2)}`);
      await onRefreshAutomationLists();
    } catch (error) {
      setOperationResult(`${label} failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" style={modalOverlayStyle}>
      <div
        ref={modalScrollRef}
        tabIndex={-1}
        className="modal-content admin-premium-modal general-settings-modal"
        style={modalContentStyle}
        onKeyDown={handleModalScrollKeyDown}
      >
        <button onClick={onClose} aria-label="Close workflow editor" className="admin-modal-close">&times;</button>
        <div style={{ paddingRight: 34 }}>
          <p className="enterprise-eyebrow">{workflow.category}</p>
          <h3 style={modalTitleStyle}>{name || workflow.key}</h3>
          <p style={modalDescriptionStyle}>{description || workflow.key}</p>
        </div>

        <WorkflowSetupGuide steps={steps} trigger={trigger} source={selectedSource} />

        <div className="workflow-editor-tabs" role="tablist" aria-label="Workflow editor sections">
          {workflowEditorPanels.map((panel, index) => (
            <button
              key={panel.key}
              type="button"
              className={activePanel === panel.key ? 'active' : ''}
              onClick={() => setActivePanel(panel.key)}
              role="tab"
              aria-selected={activePanel === panel.key}
            >
              <span>{index + 1}</span>
              <strong>{panel.label}</strong>
              <small>{panel.help}</small>
            </button>
          ))}
        </div>

        {activePanel === 'basic' && <div className="general-settings-section">
          <div className="enterprise-card-heading"><div><strong>Workflow Identity And Scope</strong><span>Name, ownership, scope and version controls</span></div></div>
          <div className="general-settings-control-grid">
            <label style={labelStyle}>Workflow Key<input value={workflow.key} disabled style={inputStyle} /></label>
            <label style={labelStyle}>Workflow Name<input value={name} disabled={!canUpdate || saving} onChange={(event) => setName(event.target.value)} style={inputStyle} /></label>
            <label style={labelStyle}>Group<select value={groupKey} disabled={!canUpdate || saving} onChange={(event) => setGroupKey(event.target.value)} style={inputStyle}>{groups.map((group) => <option key={group.key} value={group.key}>{group.name}</option>)}</select></label>
            <label style={labelStyle}>Category<select value={category} disabled={!canUpdate || saving} onChange={(event) => setCategory(event.target.value)} style={inputStyle}>{workflowCategoryOptions.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
            <label style={labelStyle}>Scope<select value={scopeType} disabled={!canUpdate || saving} onChange={(event) => setScopeType(event.target.value)} style={inputStyle}><option value="GLOBAL">GLOBAL</option><option value="CENTER">CENTER</option></select></label>
            <label style={labelStyle}>Scope Value{scopeType === 'CENTER' ? <select value={scopeValue} disabled={!canUpdate || saving} onChange={(event) => setScopeValue(event.target.value)} style={inputStyle}><option value="">Select center</option>{centers.map((center) => <option key={center} value={center}>{center}</option>)}</select> : <input value="" disabled placeholder="GLOBAL scope" style={inputStyle} />}</label>
            <label style={labelStyle}>Version<input value={`v${workflow.version}`} disabled style={inputStyle} /></label>
            <label style={labelStyle}>Status<input value={workflow.is_enabled ? 'Enabled' : 'Disabled'} disabled style={inputStyle} /></label>
            <label style={labelStyle} className="user-modal-wide">Description<textarea value={description} disabled={!canUpdate || saving} onChange={(event) => setDescription(event.target.value)} rows={3} style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} /></label>
          </div>
        </div>}

        {activePanel === 'source' && <div className="general-settings-section">
          <div className="enterprise-card-heading"><div><strong>Looker Source And Trigger</strong><span>Choose the source row and field that starts message scheduling</span></div></div>
          <div className="general-settings-control-grid">
            <label style={labelStyle}>Integration Looker Look<select
              value={selectedLookerSource?.key || ''}
              disabled={!canUpdate || saving}
              onChange={(event) => {
                const nextLook = lookerSources.find((source) => source.key === event.target.value);
                const matchingRuntimeSource = nextLook ? sources.find((source) => String(source.externalSourceId) === String(nextLook.lookId)) : undefined;
                if (matchingRuntimeSource) setTrigger((current) => ({ ...current, sourceKey: matchingRuntimeSource.key }));
                if (nextLook) void onPreviewLookerSource(nextLook.key);
              }}
              style={inputStyle}
            >
              <option value="">Select configured Look</option>
              {orderedLookerSources(lookerSources).map((source) => <option key={source.key} value={source.key}>{lookerSourceOptionLabel(source)}</option>)}
            </select><HelpText text="These options come from Integration Settings. Selecting one previews fields and links to the matching runtime source when available." /></label>
            <label style={labelStyle}>Looker Source<select value={trigger.sourceKey} disabled={!canUpdate || saving} onChange={(event) => setTrigger((current) => ({ ...current, sourceKey: event.target.value }))} style={inputStyle}>{sources.map((source) => <option key={source.key} value={source.key}>{source.name} ({source.key})</option>)}</select><HelpText text="Choose the configured Looker source that supplies rows for this workflow." /></label>
            <label style={labelStyle}>Send For<select value={trigger.triggerMode} disabled={!canUpdate || saving} onChange={(event) => setTrigger((current) => ({ ...current, triggerMode: event.target.value as AutomationTriggerContract['triggerMode'] }))} style={inputStyle}><option value="ON_NEW_EVENT">New source rows only</option><option value="ON_EVENT_CHANGE">Changed matching source rows</option></select></label>
            <label style={labelStyle}>Trigger Field<select value={trigger.anchorField} disabled={!canUpdate || saving} onChange={(event) => setTrigger((current) => ({ ...current, anchorField: event.target.value }))} style={inputStyle}>{triggerFieldOptions.map((field) => <option key={field} value={field}>{friendlyFieldLabel(field)}</option>)}</select><HelpText text="Choose the Looker date/time or status field that anchors workflow scheduling." /></label>
            <label style={labelStyle}>Ignore Old Source Rows Before<input type="datetime-local" value={toLocalDatetimeValue(trigger.activationCutoff)} disabled={!canUpdate || saving} onChange={(event) => setTrigger((current) => ({ ...current, activationCutoff: event.target.value ? new Date(event.target.value).toISOString() : undefined }))} style={inputStyle} /><HelpText text="Leave blank while drafting. Set this before activation if old source rows must not be enrolled." /></label>
          </div>
          <div className="general-settings-section source-health-card" style={{ marginTop: 12 }}>
            <div className="enterprise-card-heading">
              <div><strong>Looker Source Health</strong><span>Runtime source state and Integration Settings field preview</span></div>
              {selectedLookerSource && <button className="mini-btn" type="button" onClick={() => void onPreviewLookerSource(selectedLookerSource.key)}>Preview Fields</button>}
            </div>
            {selectedSource && <p className="general-settings-help">Runtime status: {selectedSource.status}. Last success: {selectedSource.lastSuccessAt ? new Date(selectedSource.lastSuccessAt).toLocaleString() : '-'}. Last failure: {selectedSource.lastFailureReason || '-'}. Next poll: {selectedSource.nextPollAt ? new Date(selectedSource.nextPollAt).toLocaleString() : '-'}</p>}
            {selectedLookerSource
              ? <p className="general-settings-help">Integration Settings Look: {selectedLookerSource.name} ({selectedLookerSource.key}), Look ID {selectedLookerSource.lookId || '-'}. Previewed fields: {selectedLookerTest?.fields.length ?? 0}.</p>
              : <p className="general-settings-help">No matching Integration Settings Look was found for this runtime source. Preview the Look under Integration Looker Looks, then align the runtime source to that Look ID.</p>}
            {selectedLookerTest && <InlineFieldChips fields={selectedLookerTest.fields} />}
          </div>
          <div className="row-actions" style={{ marginTop: 12 }}>
            <button className="mini-btn" disabled={!selectedLookerSource || !canUpdate} onClick={() => selectedLookerSource && onPreviewLookerSource(selectedLookerSource.key)}>Preview Fields</button>
            <button className="mini-btn pink" disabled={!selectedLookerSource || !canUpdate} onClick={() => onEditDataSource(selectedSource, selectedLookerSource)}>Create/Edit Source From This Look</button>
          </div>
        </div>}

        {activePanel === 'schedule' && <div className="general-settings-section">
          <div className="enterprise-card-heading"><div><strong>Trigger And Schedule</strong><span>Select how rows enter the workflow and when messages are calculated</span></div></div>
          <div className="general-settings-control-grid">
            <label style={labelStyle}>Looker Source<select value={trigger.sourceKey} disabled={!canUpdate || saving} onChange={(event) => setTrigger((current) => ({ ...current, sourceKey: event.target.value }))} style={inputStyle}>{sources.map((source) => <option key={source.key} value={source.key}>{source.name} ({source.key})</option>)}</select></label>
            <label style={labelStyle}>Trigger Field<select value={trigger.anchorField} disabled={!canUpdate || saving} onChange={(event) => setTrigger((current) => ({ ...current, anchorField: event.target.value }))} style={inputStyle}>{triggerFieldOptions.map((field) => <option key={field} value={field}>{friendlyFieldLabel(field)}</option>)}</select></label>
            <label style={labelStyle}>Trigger Type<select value={trigger.triggerMode} disabled={!canUpdate || saving} onChange={(event) => setTrigger((current) => ({ ...current, triggerMode: event.target.value as AutomationTriggerContract['triggerMode'] }))} style={inputStyle}><option value="ON_NEW_EVENT">Immediate for new matching source rows</option><option value="ON_EVENT_CHANGE">When matching source rows change</option></select></label>
            <label style={labelStyle}>Ignore Old Source Rows Before<input type="datetime-local" value={toLocalDatetimeValue(trigger.activationCutoff)} disabled={!canUpdate || saving} onChange={(event) => setTrigger((current) => ({ ...current, activationCutoff: event.target.value ? new Date(event.target.value).toISOString() : undefined }))} style={inputStyle} /></label>
          </div>
          <div className="general-settings-control-grid" style={{ marginTop: 12 }}>
            {steps.slice(0, 1).map((step) => (
              <div key={step.step_key} className="general-settings-section source-health-card">
                <div className="enterprise-card-heading"><div><strong>First Message Timing</strong><span>This controls the first schedule calculation after the trigger field</span></div></div>
                <div className="general-settings-control-grid">
                  <label style={labelStyle}>Delay Value<input type="number" min={0} value={step.delay_value} disabled={!canUpdate || saving} onChange={(event) => setSteps((current) => current.map((item) => item.step_key === step.step_key ? ensureStepContract({ ...item, delay_value: Number(event.target.value) }) : item))} style={inputStyle} /></label>
                  <label style={labelStyle}>Delay Unit<select value={step.delay_unit} disabled={!canUpdate || saving} onChange={(event) => setSteps((current) => current.map((item) => item.step_key === step.step_key ? ensureStepContract({ ...item, delay_unit: event.target.value as WorkflowDelayUnit }) : item))} style={inputStyle}>{delayUnitOptions.map((unit) => <option key={unit} value={unit}>{unit}</option>)}</select></label>
                </div>
              </div>
            ))}
          </div>
          <details className="general-settings-advanced">
            <summary>Conditions And Enrichment</summary>
            <ConditionEditor value={trigger.condition} disabled={!canUpdate || saving} fieldOptions={triggerFieldOptions} fieldPreviews={selectedLookerTest?.fields} onPreviewRows={() => runAction('Matching rows preview', async () => { const result = await previewAutomationWorkflowRows(workflow.key); setMatchingRowsPreview(result); return result; })} onChange={(condition) => setTrigger((current) => ({ ...current, condition }))} />
            <EnrichmentEditor value={trigger.enrichments || []} disabled={!canUpdate || saving} sources={sources} fieldOptions={triggerFieldOptions} onChange={(enrichments) => setTrigger((current) => ({ ...current, enrichments }))} />
          </details>
          <div className="row-actions" style={{ marginTop: 12 }}>
            <button className="mini-btn pink" disabled={!canUpdate || saving} onClick={() => void runAction('Save trigger', () => saveAutomationWorkflowTrigger(workflow.key, trigger))}>Save Trigger</button>
          </div>
        </div>}

        {activePanel === 'advanced' && <div className="general-settings-control-grid">
          <YesNoControl label="Workflow Enabled" value={enabled} disabled={!canUpdate || saving} onChange={setEnabled} help="Controls whether this workflow can run." />
          <YesNoControl label="Manual Send Override" value={!isPatientCommunication && manualOverride} disabled={!canUpdate || saving || isPatientCommunication} onChange={setManualOverride} help={isPatientCommunication ? 'Strict schedule is currently enforced for patient communication workflows.' : 'Yes allows users to send outside the default schedule. No keeps users on the configured schedule.'} />
        </div>}

        {activePanel === 'advanced' && sections.map(([section, sectionSettings]) => (
          <div key={section} className="general-settings-section">
            <div className="enterprise-card-heading"><div><strong>{section}</strong><span>{sectionSettings.length} settings</span></div></div>
            <div className="general-settings-control-grid">
              {sectionSettings.map((setting) => (
                <DynamicSettingInput
                  key={setting.setting_key}
                  setting={setting}
                  disabled={!canUpdate || saving}
                  onChange={(value) => setSettings((current) => current.map((item) => item.setting_key === setting.setting_key ? { ...item, setting_value_json: value } : item))}
                  onAdjustReminderCount={setting.setting_key === 'REMINDER_COUNT' ? (delta) => setSettings((current) => current.map((item) => item.setting_key === 'REMINDER_COUNT' ? { ...item, setting_value_json: { value: clamp(Number(readSettingValue(current, 'REMINDER_COUNT') || 1) + delta, minFor(setting), maxFor(setting)) } } : item)) : undefined}
                />
              ))}
            </div>
          </div>
        ))}

        {activePanel === 'conditions' && <div className="general-settings-section">
          <div className="enterprise-card-heading"><div><strong>Conditions</strong><span>Field rules that decide which source rows enter this workflow</span></div></div>
          <ConditionEditor value={trigger.condition} disabled={!canUpdate || saving} fieldOptions={triggerFieldOptions} fieldPreviews={selectedLookerTest?.fields} onPreviewRows={() => runAction('Matching rows preview', async () => { const result = await previewAutomationWorkflowRows(workflow.key); setMatchingRowsPreview(result); return result; })} onChange={(condition) => setTrigger((current) => ({ ...current, condition }))} />
          <EnrichmentEditor value={trigger.enrichments || []} disabled={!canUpdate || saving} sources={sources} fieldOptions={triggerFieldOptions} onChange={(enrichments) => setTrigger((current) => ({ ...current, enrichments }))} />
        </div>}

        {activePanel === 'messages' && <div className="general-settings-section">
          <div className="enterprise-card-heading">
            <div><strong>Message Steps</strong><span>Add, reorder, map and control WhatsApp messages</span></div>
            <div className="row-actions">
              <button className="mini-btn" onClick={() => setMessageDetailsOpen(false)}>Collapse All</button>
              <button className="mini-btn" onClick={() => setMessageDetailsOpen(true)}>Expand All</button>
              <button className="mini-btn pink" disabled={!canUpdate || saving} onClick={() => setSteps((current) => reorderSteps([...current, newStep(current.length + 1, templateOptions[0])]))}>Add Message</button>
            </div>
          </div>
          <div className="general-settings-step-list">
            {steps.map((step) => (
              <WorkflowStepRow
                key={step.step_key}
                step={step}
                disabled={!canUpdate || saving}
                fieldOptions={triggerFieldOptions}
                fieldPreviews={selectedLookerTest?.fields}
                mediaAssets={mediaAssets}
                templateOptions={templateOptions}
                channelOptions={channelOptions(controlMeta(reminderCountSetting), steps)}
                detailsOpen={messageDetailsOpen}
                onChange={(patch) => setSteps((current) => current.map((item) => item.step_key === step.step_key ? ensureStepContract({ ...item, ...patch }) : item))}
                onCopy={() => setSteps((current) => reorderSteps([...current, cloneStep(step, current.length + 1)]))}
                onRemove={() => setSteps((current) => reorderSteps(current.filter((item) => item.step_key !== step.step_key)))}
                onMove={(direction) => setSteps((current) => moveStep(current, step.step_key, direction))}
              />
            ))}
          </div>
        </div>}

        {activePanel === 'mapping' && <div className="general-settings-section">
          <div className="enterprise-card-heading"><div><strong>Template Mapping</strong><span>Map selected message template slots to Looker fields, static values, computed values and media</span></div></div>
          <div className="general-settings-step-list">
            {steps.map((step) => (
              <WorkflowStepRow
                key={step.step_key}
                step={step}
                disabled={!canUpdate || saving}
                fieldOptions={triggerFieldOptions}
                fieldPreviews={selectedLookerTest?.fields}
                mediaAssets={mediaAssets}
                templateOptions={templateOptions}
                channelOptions={channelOptions(controlMeta(reminderCountSetting), steps)}
                detailsOpen={messageDetailsOpen}
                onChange={(patch) => setSteps((current) => current.map((item) => item.step_key === step.step_key ? ensureStepContract({ ...item, ...patch }) : item))}
                onCopy={() => setSteps((current) => reorderSteps([...current, cloneStep(step, current.length + 1)]))}
                onRemove={() => setSteps((current) => reorderSteps(current.filter((item) => item.step_key !== step.step_key)))}
                onMove={(direction) => setSteps((current) => moveStep(current, step.step_key, direction))}
              />
            ))}
          </div>
          <TemplateBindingPreview steps={steps} />
        </div>}

        {activePanel === 'preview' && <div className="general-settings-section">
          <div className="enterprise-card-heading"><div><strong>Preview And Activation</strong><span>Schedule, condition, template binding and pending impact preview</span></div></div>
          <div className="general-settings-control-grid">
            <label style={labelStyle}>Sample Trigger Timestamp<input type="datetime-local" value={sampleTimestamp} disabled={saving} onChange={(event) => setSampleTimestamp(event.target.value)} style={inputStyle} /></label>
          </div>
          <div style={previewGridStyle}>{preview.map((item) => <div key={item.label} className="general-settings-preview-card"><span>{item.label}</span><strong>{item.date}</strong></div>)}</div>
          <div className="row-actions" style={{ marginTop: 12 }}>
            <button className="mini-btn" disabled={saving} onClick={() => void runAction('Matching rows preview', async () => { const result = await previewAutomationWorkflowRows(workflow.key); setMatchingRowsPreview(result); return result; })}>Preview Matching Rows</button>
            <button className="mini-btn" disabled={saving} onClick={() => void runAction('Schedule preview', async () => { const result = await previewAutomationWorkflowSchedule(workflow.key, { triggerTimestamp: sampleTimestamp ? new Date(sampleTimestamp).toISOString() : undefined, context: sampleContext(trigger) }); setSchedulePreview(result); return result; })}>Schedule Preview</button>
            <button className="mini-btn" disabled={saving} onClick={() => void runAction('Template preview', async () => { const result = await previewAutomationWorkflowTemplate(workflow.key, { stepKey: steps.find((step) => step.is_active)?.step_key, context: sampleContext(trigger) }); setTemplatePreview(result); return result; })}>Preview Template</button>
            <button className="mini-btn" disabled={saving} onClick={() => void runAction('Pending impact', async () => { const result = await getAutomationWorkflowPendingImpact(workflow.key); setPendingImpact(result); return result; })}>Pending Impact</button>
            <button className="mini-btn" disabled={saving} onClick={() => void runAction('Validate activation', async () => { const result = await validateAutomationWorkflow(workflow.key); setValidation(result); return result; })}>Validate</button>
            <button className="mini-btn pink" disabled={!canUpdate || saving} onClick={() => void runAction('Activate workflow', () => activateAutomationWorkflow(workflow.key))}>Activate</button>
            <button className="mini-btn" disabled={!canUpdate || saving} onClick={() => void runAction('Deactivate workflow', () => deactivateAutomationWorkflow(workflow.key))}>Deactivate</button>
          </div>
          <PreviewJson title="Validation" value={validation} />
          <PreviewJson title="Matching Rows Preview" value={matchingRowsPreview} />
          <PreviewJson title="Pending Impact" value={pendingImpact} />
          <PreviewJson title="Schedule Preview" value={schedulePreview} />
          <PreviewJson title="Template Preview" value={templatePreview} />
          {operationResult && <pre className="general-settings-json-preview">{operationResult}</pre>}
        </div>}

        <div style={modalActionsStyle}>
          <button className="btn btn-outline" disabled={activePanelIndex <= 0} onClick={() => setActivePanel(workflowEditorPanels[Math.max(0, activePanelIndex - 1)].key)}>Back</button>
          <button className="btn btn-outline" disabled={activePanelIndex >= workflowEditorPanels.length - 1} onClick={() => setActivePanel(workflowEditorPanels[Math.min(workflowEditorPanels.length - 1, activePanelIndex + 1)].key)}>Next</button>
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={!canUpdate || saving}>{saving ? 'Saving...' : 'Save Workflow'}</button>
        </div>
      </div>
    </div>
  );
}

function WorkflowSetupGuide({ steps, trigger, source }: { steps: AutomationWorkflowStep[]; trigger: AutomationTriggerContract; source?: AutomationDataSourceSummary }) {
  const activeSteps = steps.filter((step) => step.is_active).sort((a, b) => a.step_order - b.step_order);
  return (
    <div className="workflow-easy-guide">
      <div>
        <span>1</span>
        <strong>Looker Source</strong>
        <small>{source?.name || trigger.sourceKey || 'Select Looker source'}</small>
      </div>
      <div>
        <span>2</span>
        <strong>Trigger</strong>
        <small>{friendlyFieldLabel(trigger.anchorField)}</small>
      </div>
      <div>
        <span>3</span>
        <strong>Message</strong>
        <small>{activeSteps.length} active message{activeSteps.length === 1 ? '' : 's'}</small>
      </div>
      <div>
        <span>4</span>
        <strong>Conditions</strong>
        <small>{trigger.condition ? 'Rules configured' : 'No rules'}</small>
      </div>
      <div>
        <span>5</span>
        <strong>Preview</strong>
        <small>Validate before activation</small>
      </div>
    </div>
  );
}

function InlineFieldChips({ fields }: { fields: ConfiguredLookerFieldPreview[] }) {
  return (
    <div className="looker-field-chip-row">
      {fields.slice(0, 12).map((field) => (
        <span key={field.fieldKey} className={field.isDateTimeCandidate ? 'date-field' : ''} title={field.fieldKey}>
          {field.fieldLabel}
        </span>
      ))}
      {fields.length > 12 && <span>+{fields.length - 12} more</span>}
    </div>
  );
}

function DynamicSettingInput({ setting, disabled, onChange, onAdjustReminderCount }: { setting: AutomationWorkflowSetting; disabled: boolean; onChange: (value: unknown) => void; onAdjustReminderCount?: (delta: number) => void }) {
  const meta = controlMeta(setting);
  const label = setting.setting_name || setting.setting_key;
  const value = setting.setting_value_json;
  const control = meta.control || inferControl(setting);
  if (control === 'duration') {
    const duration = durationValue(value);
    const units = meta.units?.length ? meta.units : [duration.unit];
    return <label style={labelStyle}>{label}<div className="general-settings-duration-control"><select value={duration.unit} disabled={disabled} onChange={(event) => onChange({ ...duration, unit: event.target.value })} style={inputStyle}>{units.map((unit) => <option key={unit} value={unit}>{unit}</option>)}</select><input type="number" min={meta.min || 1} max={meta.max} value={duration.value} disabled={disabled} onChange={(event) => onChange({ ...duration, value: Number(event.target.value) })} style={inputStyle} /></div><HelpText text={meta.helpText} /></label>;
  }
  if (control === 'toggle') {
    return <YesNoControl label={label} value={Boolean((value as { value?: boolean })?.value ?? value)} disabled={disabled} onChange={(next) => onChange({ value: next })} help={meta.helpText} />;
  }
  if (control === 'select' || control === 'radio') {
    const options = selectOptions(meta, value);
    const current = String((value as { value?: unknown })?.value ?? '');
    return <label style={labelStyle}>{label}<select value={current} disabled={disabled} onChange={(event) => onChange({ value: event.target.value })} style={inputStyle}>{options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select><HelpText text={meta.helpText} /></label>;
  }
  if (control === 'textarea') {
    return <label style={labelStyle}>{label}<textarea value={String((value as { value?: unknown })?.value ?? '')} disabled={disabled} onChange={(event) => onChange({ value: event.target.value })} rows={4} style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} /><HelpText text={meta.helpText} /></label>;
  }
  if (control === 'text') {
    return <label style={labelStyle}>{label}<input type="text" value={String((value as { value?: unknown })?.value ?? '')} disabled={disabled} onChange={(event) => onChange({ value: event.target.value })} style={inputStyle} /><HelpText text={meta.helpText} /></label>;
  }
  const rawNumberValue = Number((value as { value?: unknown })?.value ?? value ?? meta.min ?? 1);
  const numberValue = Number.isFinite(rawNumberValue) ? rawNumberValue : (meta.min ?? 1);
  return <label style={labelStyle}>{label}<div className="general-settings-number-control"><input type="number" min={meta.min || 1} max={meta.max} value={numberValue} disabled={disabled} onChange={(event) => onChange({ value: Number(event.target.value) })} style={inputStyle} />{onAdjustReminderCount && <><button type="button" className="mini-btn" disabled={disabled} onClick={() => onAdjustReminderCount(-1)}>-</button><button type="button" className="mini-btn" disabled={disabled} onClick={() => onAdjustReminderCount(1)}>+</button></>}</div><HelpText text={meta.helpText} /></label>;
}

function ConditionEditor({
  value,
  disabled,
  fieldOptions,
  fieldPreviews,
  onPreviewRows,
  onChange,
}: {
  value?: AutomationConditionNode;
  disabled: boolean;
  fieldOptions: string[];
  fieldPreviews?: ConfiguredLookerFieldPreview[];
  onPreviewRows?: () => void;
  onChange: (value?: AutomationConditionNode) => void;
}) {
  const group = conditionGroup(value);
  const firstField = fieldOptions[0] || 'trigger.occurredAt';
  return (
    <div className="general-settings-section" style={{ marginTop: 12 }}>
      <div className="enterprise-card-heading">
        <div><strong>Conditions</strong><span>AND / OR field rules for enrollment</span></div>
        <div className="row-actions">
          <button className="mini-btn" disabled={disabled} onClick={() => onChange({ ...group, conditions: [...group.conditions, defaultPredicate(firstField)] })}>Add Condition</button>
          <button className="mini-btn" disabled={disabled} onClick={() => onChange({ ...group, conditions: [...group.conditions, { type: 'GROUP', combinator: 'AND', conditions: [defaultPredicate(firstField)] }] })}>Add Group</button>
          {onPreviewRows && <button className="mini-btn" onClick={onPreviewRows}>Preview Matching Rows</button>}
          <button className="mini-btn" disabled={disabled || group.conditions.length === 0} onClick={() => onChange(undefined)}>Clear Conditions</button>
        </div>
      </div>
      <div className="general-settings-control-grid">
        <label style={labelStyle}>Match<select value={group.combinator} disabled={disabled} onChange={(event) => onChange({ ...group, combinator: event.target.value as 'AND' | 'OR' })} style={inputStyle}><option value="AND">AND</option><option value="OR">OR</option></select></label>
      </div>
      <div className="general-settings-step-list">
        {group.conditions.map((condition, index) => {
          if (condition.type === 'GROUP') {
            return (
              <ConditionGroupCard
                key={`group-${index}`}
                group={condition}
                disabled={disabled}
                fieldOptions={fieldOptions}
                fieldPreviews={fieldPreviews}
                onChange={(next) => onChange({ ...group, conditions: updateArrayItem(group.conditions, index, next) })}
                onRemove={() => onChange({ ...group, conditions: group.conditions.filter((_, itemIndex) => itemIndex !== index) })}
              />
            );
          }
          const predicate = condition;
          return (
            <div key={`${predicate.field}-${index}`} className="general-settings-step-row condition-row">
              <ConditionPredicateFields
                predicate={predicate}
                disabled={disabled}
                fieldOptions={fieldOptions}
                fieldPreviews={fieldPreviews}
                onChange={(patch) => onChange(updatePredicate(group, index, patch))}
              />
              <button className="mini-btn" disabled={disabled} onClick={() => onChange({ ...group, conditions: group.conditions.filter((_, itemIndex) => itemIndex !== index) })}>Remove</button>
            </div>
          );
        })}
        {!group.conditions.length && <p className="general-settings-help">No conditions. Workflow enrolls every matching trigger event.</p>}
      </div>
    </div>
  );
}

function ConditionGroupCard({
  group,
  disabled,
  fieldOptions,
  fieldPreviews,
  onChange,
  onRemove,
}: {
  group: Extract<AutomationConditionNode, { type: 'GROUP' }>;
  disabled: boolean;
  fieldOptions: string[];
  fieldPreviews?: ConfiguredLookerFieldPreview[];
  onChange: (value: Extract<AutomationConditionNode, { type: 'GROUP' }>) => void;
  onRemove: () => void;
}) {
  const firstField = fieldOptions[0] || 'trigger.occurredAt';
  return (
    <div className="general-settings-section condition-group-card">
      <div className="enterprise-card-heading">
        <div><strong>Condition Group</strong><span>Nested rule block</span></div>
        <div className="row-actions">
          <button className="mini-btn" disabled={disabled} onClick={() => onChange({ ...group, conditions: [...group.conditions, defaultPredicate(firstField)] })}>Add Condition</button>
          <button className="mini-btn" disabled={disabled} onClick={onRemove}>Remove Group</button>
        </div>
      </div>
      <label style={labelStyle}>Match<select value={group.combinator} disabled={disabled} onChange={(event) => onChange({ ...group, combinator: event.target.value as 'AND' | 'OR' })} style={inputStyle}><option value="AND">AND</option><option value="OR">OR</option></select></label>
      <div className="general-settings-step-list">
        {group.conditions.map((condition, index) => {
          const predicate = condition.type === 'PREDICATE' ? condition : defaultPredicate(firstField);
          return (
            <div key={`${predicate.field}-${index}`} className="general-settings-step-row condition-row">
              <ConditionPredicateFields
                predicate={predicate}
                disabled={disabled}
                fieldOptions={fieldOptions}
                fieldPreviews={fieldPreviews}
                onChange={(patch) => onChange(updatePredicate(group, index, patch))}
              />
              <button className="mini-btn" disabled={disabled} onClick={() => onChange({ ...group, conditions: group.conditions.filter((_, itemIndex) => itemIndex !== index) })}>Remove</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ConditionPredicateFields({
  predicate,
  disabled,
  fieldOptions,
  fieldPreviews,
  onChange,
}: {
  predicate: Extract<AutomationConditionNode, { type: 'PREDICATE' }>;
  disabled: boolean;
  fieldOptions: string[];
  fieldPreviews?: ConfiguredLookerFieldPreview[];
  onChange: (patch: Partial<Extract<AutomationConditionNode, { type: 'PREDICATE' }>>) => void;
}) {
  const noValue = noValueConditionOperators.has(predicate.operator);
  const operatorOptions = [...new Set([predicate.operator, ...conditionOperatorsForField(predicate.field, fieldPreviews)])];
  const sampleValues = conditionSampleValues(predicate.field, fieldPreviews);
  const listId = `condition-values-${stableId(predicate.field)}`;
  return (
    <>
      <select value={predicate.field} disabled={disabled} onChange={(event) => onChange({ field: event.target.value })} style={inputStyle}>{fieldOptions.map((field) => <option key={field} value={field}>{friendlyFieldLabel(field)}</option>)}</select>
      <select value={predicate.operator} disabled={disabled} onChange={(event) => {
        const operator = event.target.value as AutomationConditionOperator;
        onChange({ operator, value: defaultConditionValue(operator) });
      }} style={inputStyle}>{operatorOptions.map((operator) => <option key={operator} value={operator}>{conditionOperatorLabel(operator)}</option>)}</select>
      {!noValue && (
        <>
          <input
            list={sampleValues.length ? listId : undefined}
            type={conditionInputType(predicate.operator)}
            value={predicateValueText(predicate.value, predicate.operator)}
            disabled={disabled}
            onChange={(event) => onChange({ value: parsePredicateValue(event.target.value, predicate.operator) })}
            placeholder={conditionPlaceholder(predicate.operator)}
            style={inputStyle}
          />
          {sampleValues.length > 0 && <datalist id={listId}>{sampleValues.map((value) => <option key={value} value={value} />)}</datalist>}
        </>
      )}
    </>
  );
}

function EnrichmentEditor({ value, disabled, sources, fieldOptions, onChange }: { value: NonNullable<AutomationTriggerContract['enrichments']>; disabled: boolean; sources: AutomationDataSourceSummary[]; fieldOptions: string[]; onChange: (value: NonNullable<AutomationTriggerContract['enrichments']>) => void }) {
  return (
    <div className="general-settings-section" style={{ marginTop: 12 }}>
      <div className="enterprise-card-heading"><div><strong>Enrichment Source Rows</strong><span>Optional additional Looker sources for template variables</span></div><button className="mini-btn" disabled={disabled} onClick={() => onChange([...value, { alias: `enrichment_${value.length + 1}`, sourceKey: sources[0]?.key || '', triggerField: fieldOptions[0] || 'trigger.patientId', enrichmentField: fieldOptions[0] || 'trigger.patientId', required: false }])}>Add Enrichment</button></div>
      <div className="general-settings-step-list">
        {value.map((row, index) => (
          <div key={`${row.alias}-${index}`} className="general-settings-step-row">
            <input value={row.alias} disabled={disabled} onChange={(event) => onChange(updateArrayItem(value, index, { ...row, alias: event.target.value }))} placeholder="Alias" style={inputStyle} />
            <select value={row.sourceKey} disabled={disabled} onChange={(event) => onChange(updateArrayItem(value, index, { ...row, sourceKey: event.target.value }))} style={inputStyle}>{sources.map((source) => <option key={source.key} value={source.key}>{source.key}</option>)}</select>
            <select value={row.triggerField} disabled={disabled} onChange={(event) => onChange(updateArrayItem(value, index, { ...row, triggerField: event.target.value }))} style={inputStyle}>{fieldOptions.map((field) => <option key={field} value={field}>{field}</option>)}</select>
            <input value={row.enrichmentField} disabled={disabled} onChange={(event) => onChange(updateArrayItem(value, index, { ...row, enrichmentField: event.target.value }))} placeholder="Enrichment field" style={inputStyle} />
            <YesNoControl label="Required" value={row.required} disabled={disabled} compact onChange={(required) => onChange(updateArrayItem(value, index, { ...row, required }))} />
            <button className="mini-btn" disabled={disabled} onClick={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}>Remove</button>
          </div>
        ))}
        {!value.length && <p className="general-settings-help">No enrichment source configured.</p>}
      </div>
    </div>
  );
}

function WorkflowStepRow({
  step,
  disabled,
  channelOptions,
  templateOptions,
  fieldOptions,
  fieldPreviews,
  mediaAssets,
  detailsOpen,
  onChange,
  onCopy,
  onRemove,
  onMove,
}: {
  step: AutomationWorkflowStep;
  disabled: boolean;
  channelOptions: WorkflowChannel[];
  templateOptions: TemplateOption[];
  fieldOptions: string[];
  fieldPreviews?: ConfiguredLookerFieldPreview[];
  mediaAssets: CommunicationMediaAssetSummary[];
  detailsOpen: boolean;
  onChange: (patch: Partial<AutomationWorkflowStep>) => void;
  onCopy: () => void;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
}) {
  const filteredTemplates = templateOptions.filter((template) => template.channel === step.channel);
  const selectedTemplateKey = step.template_setting_key || '';
  const contract = stepContract(step);
  const selectedTemplate = templateOptions.find((template) => template.key === selectedTemplateKey);
  return (
    <div className="general-settings-section workflow-message-card">
      <div className="enterprise-card-heading">
        <div><strong>Message {step.step_order}: {step.step_name}</strong><span>{messageSummary(contract, selectedTemplate)}</span></div>
        <div className="row-actions">
          <button className="mini-btn" disabled={disabled} onClick={() => onMove(-1)}>Up</button>
          <button className="mini-btn" disabled={disabled} onClick={() => onMove(1)}>Down</button>
          <button className="mini-btn" disabled={disabled} onClick={onCopy}>Copy</button>
          <button className="mini-btn" disabled={disabled} onClick={onRemove}>Remove</button>
        </div>
      </div>
      <div className="workflow-message-chip-row">
        <span className={step.is_active ? 'ready' : 'missing'}>{step.is_active ? 'Active' : 'Inactive'}</span>
        <span>{selectedTemplate?.label || 'No template selected'}</span>
        <span>{contract.timing.direction || 'AFTER'} {contract.timing.delayValue} {friendlyUnit(contract.timing.delayUnit)} {contract.timing.anchor === 'PREVIOUS_STEP_COMPLETED_AT' ? 'previous step' : 'trigger'}</span>
        <span>{contract.action.bindings.length} template binding{contract.action.bindings.length === 1 ? '' : 's'}</span>
      </div>
      <div className="workflow-message-main">
        <TextField label="Message Name" value={step.step_name} disabled={disabled} onChange={(step_name) => onChange({ step_name, metadata_json: { contract: { ...contract, stepName: step_name } } })} />
        <label style={labelStyle}>Delay<input type="number" min={0} value={contract.timing.delayValue} disabled={disabled} onChange={(event) => onChangeContract(onChange, step, { ...contract, timing: { ...contract.timing, delayValue: Number(event.target.value) } })} style={inputStyle} /></label>
        <label style={labelStyle}>Period<select value={contract.timing.delayUnit} disabled={disabled} onChange={(event) => onChangeContract(onChange, step, { ...contract, timing: { ...contract.timing, delayUnit: event.target.value as WorkflowDelayUnit } })} style={inputStyle}>{['HOURS', 'DAYS', 'WEEKS', 'MONTHS', 'YEARS', 'MINUTES', 'SECONDS'].map((unit) => <option key={unit} value={unit}>{friendlyUnit(unit)}</option>)}</select></label>
        <label style={labelStyle}>Direction<select value={contract.timing.direction || 'AFTER'} disabled={disabled} onChange={(event) => onChangeContract(onChange, step, { ...contract, timing: { ...contract.timing, direction: event.target.value as 'AFTER' | 'BEFORE' } })} style={inputStyle}><option value="AFTER">After trigger</option><option value="BEFORE">Before trigger</option></select></label>
        <label style={labelStyle}>Count From<select value={contract.timing.anchor} disabled={disabled} onChange={(event) => onChangeContract(onChange, step, { ...contract, timing: { ...contract.timing, anchor: event.target.value as AutomationTimingAnchor } })} style={inputStyle}><option value="TRIGGER_TIMESTAMP">Selected trigger field time</option><option value="ENROLLMENT_TIMESTAMP">Workflow enrollment time</option><option value="PREVIOUS_STEP_COMPLETED_AT">Previous message completed</option></select></label>
        <label style={labelStyle}>Fixed Send Time<input type="time" value={contract.timing.fixedTime || ''} disabled={disabled} onChange={(event) => onChangeContract(onChange, step, { ...contract, timing: { ...contract.timing, fixedTime: event.target.value || undefined } })} style={inputStyle} /><HelpText text="Optional. Uses this IST clock time after applying delay/direction." /></label>
        <label style={labelStyle} className="user-modal-wide">WhatsApp Template<select
          value={selectedTemplateKey}
          disabled={disabled || filteredTemplates.length === 0}
          onChange={(event) => {
            const template = filteredTemplates.find((item) => item.key === event.target.value);
            const nextContract = {
              ...contract,
              action: {
                ...contract.action,
                templateSettingKey: template?.key || '',
                templateName: template?.templateName || undefined,
                languageCode: template?.languageCode || undefined,
              },
            };
            onChangeContract(onChange, step, nextContract, { template_setting_key: template?.key || null, template_name: template?.templateName || null });
          }}
          style={inputStyle}
        >
          <option value="">{filteredTemplates.length ? 'Select approved template' : 'No active templates'}</option>
          {filteredTemplates.map((template) => <option key={template.key} value={template.key}>{template.label}</option>)}
        </select><HelpText text="Pick the approved WhatsApp template for this message." /></label>
        <YesNoControl label="Message Active" value={step.is_active} disabled={disabled} onChange={(value) => onChangeContract(onChange, step, { ...contract, isActive: value }, { is_active: value })} compact />
      </div>
      <details className="general-settings-advanced" open={detailsOpen}>
        <summary>Delivery And Failure Handling, Conditions And Template Mapping</summary>
        <div className="general-settings-control-grid">
          <TextField label="Step Key" value={step.step_key} disabled={disabled} onChange={(step_key) => onChange({ step_key, metadata_json: { contract: { ...contract, stepKey: step_key } } })} />
          <label style={labelStyle}>Channel<select value={step.channel} disabled={disabled} onChange={(event) => {
            const channel = event.target.value as WorkflowChannel;
            onChangeContract(onChange, step, { ...contract, action: { ...contract.action, channel: 'WHATSAPP' } }, { channel });
          }} style={inputStyle}>{channelOptions.map((channel) => <option key={channel} value={channel}>{channel}</option>)}</select></label>
          <label style={labelStyle}>Retry Attempts<input type="number" min={1} value={contract.action.retry.maxAttempts} disabled={disabled} onChange={(event) => onChangeContract(onChange, step, { ...contract, action: { ...contract.action, retry: { ...contract.action.retry, maxAttempts: Number(event.target.value) } } })} style={inputStyle} /></label>
          <label style={labelStyle}>Retry Strategy<select value={contract.action.retry.strategy} disabled={disabled} onChange={(event) => onChangeContract(onChange, step, { ...contract, action: { ...contract.action, retry: { ...contract.action.retry, strategy: event.target.value as 'FIXED' | 'EXPONENTIAL' } } })} style={inputStyle}><option value="FIXED">FIXED</option><option value="EXPONENTIAL">EXPONENTIAL</option></select></label>
          <label style={labelStyle}>Initial Retry Delay Seconds<input type="number" min={0} value={contract.action.retry.initialDelaySeconds} disabled={disabled} onChange={(event) => onChangeContract(onChange, step, { ...contract, action: { ...contract.action, retry: { ...contract.action.retry, initialDelaySeconds: Number(event.target.value) } } })} style={inputStyle} /></label>
          <label style={labelStyle}>Max Retry Delay Seconds<input type="number" min={0} value={contract.action.retry.maxDelaySeconds} disabled={disabled} onChange={(event) => onChangeContract(onChange, step, { ...contract, action: { ...contract.action, retry: { ...contract.action.retry, maxDelaySeconds: Number(event.target.value) } } })} style={inputStyle} /></label>
          <label style={labelStyle} className="user-modal-wide">Retryable Failure Codes<input value={contract.action.retry.retryableFailureCodes.join(', ')} disabled={disabled} onChange={(event) => onChangeContract(onChange, step, { ...contract, action: { ...contract.action, retry: { ...contract.action.retry, retryableFailureCodes: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) } } })} style={inputStyle} /></label>
          <label style={labelStyle}>Dependency<select value={contract.action.dependency} disabled={disabled} onChange={(event) => onChangeContract(onChange, step, { ...contract, action: { ...contract.action, dependency: event.target.value as AutomationDependencyPolicy } })} style={inputStyle}><option value="INDEPENDENT">INDEPENDENT</option><option value="PREVIOUS_STEP_SUCCESS">PREVIOUS_STEP_SUCCESS</option><option value="PREVIOUS_STEP_TERMINAL">PREVIOUS_STEP_TERMINAL</option></select></label>
          <YesNoControl label="Stop On Failure" value={contract.action.stop.stopOnConfirmedFailure} disabled={disabled} compact onChange={(value) => onChangeContract(onChange, step, { ...contract, action: { ...contract.action, stop: { ...contract.action.stop, stopOnConfirmedFailure: value } } })} />
          <YesNoControl label="Require WhatsApp Phone" value={contract.action.suppression.requireWhatsappPhone} disabled={disabled} compact onChange={(value) => onChangeContract(onChange, step, { ...contract, action: { ...contract.action, suppression: { ...contract.action.suppression, requireWhatsappPhone: value } } })} />
          <YesNoControl label="Respect Opt Out" value={contract.action.suppression.respectPatientOptOut} disabled={disabled} compact onChange={(value) => onChangeContract(onChange, step, { ...contract, action: { ...contract.action, suppression: { ...contract.action.suppression, respectPatientOptOut: value } } })} />
        </div>
        <ConditionEditor value={contract.condition} disabled={disabled} fieldOptions={fieldOptions} fieldPreviews={fieldPreviews} onChange={(condition) => onChangeContract(onChange, step, { ...contract, condition })} />
        <TemplateMappingEditor
          contract={contract}
          template={selectedTemplate}
          disabled={disabled}
          fieldOptions={fieldOptions}
          mediaAssets={mediaAssets}
          onChange={(next) => onChangeContract(onChange, step, next)}
        />
      </details>
    </div>
  );
}

function TemplateMappingEditor({ contract, template, disabled, fieldOptions, mediaAssets, onChange }: { contract: AutomationWorkflowStepContract; template?: TemplateOption; disabled: boolean; fieldOptions: string[]; mediaAssets: CommunicationMediaAssetSummary[]; onChange: (contract: AutomationWorkflowStepContract) => void }) {
  const bindings = contract.action.bindings || [];
  const slots = template ? templateSlots(template) : [];
  const [mappingResult, setMappingResult] = useState<unknown>(null);
  const [checkingMapping, setCheckingMapping] = useState(false);
  const syncSlots = () => {
    const existing = new Map(bindings.map((binding) => [bindingKey(binding.address), binding]));
    const next = slots.map((slot) => existing.get(bindingKey(slot)) || defaultBinding(slot, fieldOptions[0]));
    onChange({ ...contract, action: { ...contract.action, bindings: next } });
  };
  const addManualSlot = () => {
    const address: TemplateComponentAddress = { component: 'BODY', componentIndex: 0, parameterIndex: bindings.length, parameterType: 'TEXT' };
    onChange({ ...contract, action: { ...contract.action, bindings: [...bindings, defaultBinding(address, fieldOptions[0])] } });
  };
  const updateBinding = (index: number, patch: Partial<TemplateParameterBinding>) => {
    const next = bindings.map((binding, itemIndex) => itemIndex === index ? { ...binding, ...patch } : binding);
    onChange({ ...contract, action: { ...contract.action, bindings: next } });
  };
  const runMappingCheck = async (mode: 'validate' | 'preview') => {
    if (!template) return;
    setCheckingMapping(true);
    try {
      const result = mode === 'validate'
        ? await validateAutomationTemplateMapping(template.key, bindings)
        : await previewAutomationTemplate(template.key, { bindings, context: sampleContext({ sourceKey: '', triggerMode: 'ON_NEW_EVENT', anchorField: 'trigger.registrationTimestamp' }) });
      setMappingResult(result);
    } catch (error) {
      setMappingResult({ success: false, message: error instanceof Error ? error.message : 'Template mapping check failed.' });
    } finally {
      setCheckingMapping(false);
    }
  };
  return (
    <div className="general-settings-section" style={{ marginTop: 12 }}>
      <div className="enterprise-card-heading">
        <div><strong>Template Mapping</strong><span>Header, body, footer and button slots with trigger/enrichment/static/computed/media bindings</span></div>
        <div className="row-actions">
          <button className="mini-btn" disabled={disabled || !template} onClick={syncSlots}>Sync Template Slots</button>
          <button className="mini-btn" disabled={disabled} onClick={addManualSlot}>Add Manual Slot</button>
          <button className="mini-btn" disabled={!template || checkingMapping} onClick={() => void runMappingCheck('validate')}>Validate Mapping</button>
          <button className="mini-btn" disabled={!template || checkingMapping} onClick={() => void runMappingCheck('preview')}>Preview Binding</button>
        </div>
      </div>
      {template && <p className="general-settings-help">Selected template: {template.label} | Provider name: {template.templateName || '-'} | Language: {template.languageCode || '-'}. Template schema slots detected: {slots.length}. {slots.length === 0 ? 'No synced component schema was found; add bindings manually after template sync metadata is available.' : 'Use Sync Template Slots to create/update binding rows.'}</p>}
      <div className="general-settings-step-list">
        {bindings.map((binding, index) => (
          <div key={`${bindingKey(binding.address)}-${index}`} className="template-binding-card">
            <div><strong>{templateBindingLabel(binding.address)}</strong><small>{bindingKey(binding.address)}</small></div>
            <label style={labelStyle}>Binding Type<select value={binding.source} disabled={disabled} onChange={(event) => updateBinding(index, { source: event.target.value as TemplateBindingSource })} style={inputStyle}>{bindingSources.map((source) => <option key={source} value={source}>{source.replaceAll('_', ' ')}</option>)}</select></label>
            {['TRIGGER_FIELD', 'ENRICHMENT_FIELD', 'MEDIA_URL_FIELD'].includes(binding.source) && (
              <label style={labelStyle}>Source Field<select value={binding.sourcePath || ''} disabled={disabled} onChange={(event) => updateBinding(index, { sourcePath: event.target.value })} style={inputStyle}>{fieldOptions.map((field) => <option key={field} value={field}>{friendlyFieldLabel(field)}</option>)}</select></label>
            )}
            {binding.source === 'STATIC_VALUE' && <label style={labelStyle}>Static Value<input value={binding.value || ''} disabled={disabled} onChange={(event) => updateBinding(index, { value: event.target.value })} placeholder="Static value" style={inputStyle} /></label>}
            {binding.source === 'COMPUTED_VALUE' && <label style={labelStyle}>Computed Value<select value={binding.computedKey || ''} disabled={disabled} onChange={(event) => updateBinding(index, { computedKey: event.target.value })} style={inputStyle}><option value="">Select computed value</option>{computedTemplateValues.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>}
            {binding.source === 'MEDIA_ASSET' && (
              <label style={labelStyle}>Media Asset<select value={binding.mediaAssetKey || ''} disabled={disabled} onChange={(event) => updateBinding(index, { mediaAssetKey: event.target.value })} style={inputStyle}>
                <option value="">Select media asset</option>
                {mediaAssets.filter((asset) => asset.mediaType === binding.address.parameterType).map((asset) => <option key={`${asset.key}-${asset.version}`} value={asset.key}>{asset.name} ({asset.validationStatus}, {asset.mimeType || asset.mediaType}, {asset.fileSizeBytes ? `${Math.round(Number(asset.fileSizeBytes) / 1024)} KB` : 'size unknown'})</option>)}
              </select></label>
            )}
            {binding.source === 'PROVIDER_MEDIA_ID' && <label style={labelStyle}>Provider Media ID<input value={binding.value || ''} disabled={disabled} onChange={(event) => updateBinding(index, { value: event.target.value })} placeholder="Provider media ID" style={inputStyle} /></label>}
            <label style={labelStyle}>Fallback<input value={binding.fallbackValue || ''} disabled={disabled} onChange={(event) => updateBinding(index, { fallbackValue: event.target.value })} placeholder="Fallback" style={inputStyle} /></label>
            <YesNoControl label="Required" value={binding.required} disabled={disabled} compact onChange={(required) => updateBinding(index, { required })} />
            <button className="mini-btn" disabled={disabled} onClick={() => onChange({ ...contract, action: { ...contract.action, bindings: bindings.filter((_, itemIndex) => itemIndex !== index) } })}>Remove</button>
          </div>
        ))}
        {!bindings.length && <p className="general-settings-help">No template bindings configured yet.</p>}
      </div>
      <PreviewJson title="Template Mapping Check" value={mappingResult} />
    </div>
  );
}

function MediaAssetEditor({ asset, canUpdate, onClose, onSave }: { asset?: CommunicationMediaAssetSummary; canUpdate: boolean; onClose: () => void; onSave: (payload: CommunicationMediaAssetContract) => Promise<void> }) {
  const [payload, setPayload] = useState<CommunicationMediaAssetContract>({
    key: asset?.key || '',
    version: asset?.version || 1,
    name: asset?.name || '',
    mediaType: (asset?.mediaType || 'IMAGE') as CommunicationMediaAssetContract['mediaType'],
    sourceType: (asset?.sourceType || 'PUBLIC_HTTPS_URL') as CommunicationMediaAssetContract['sourceType'],
    mimeType: asset?.mimeType || '',
    fileSizeBytes: asset?.fileSizeBytes ? Number(asset.fileSizeBytes) : undefined,
    scopeType: (asset?.scopeType || 'GLOBAL') as CommunicationMediaAssetContract['scopeType'],
    scopeValue: asset?.scopeValue || '',
    isActive: Boolean(asset?.isActive),
  });
  const [publicUrl, setPublicUrl] = useState(asset?.publicUrl || '');
  const [providerMediaId, setProviderMediaId] = useState(asset?.providerMediaId || '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        ...payload,
        publicUrl: payload.sourceType === 'PUBLIC_HTTPS_URL' ? publicUrl || undefined : undefined,
        providerMediaId: payload.sourceType === 'PROVIDER_MEDIA_ID' ? providerMediaId || undefined : undefined,
        mimeType: payload.mimeType || undefined,
        fileSizeBytes: payload.fileSizeBytes || undefined,
        scopeValue: payload.scopeType === 'CENTER' ? payload.scopeValue : undefined,
      });
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="modal-overlay" style={modalOverlayStyle}>
      <div className="modal-content admin-premium-modal" style={modalContentStyle}>
        <button onClick={onClose} className="admin-modal-close">&times;</button>
        <h3 style={modalTitleStyle}>{asset ? 'Edit Media Asset' : 'Add Media Asset'}</h3>
        <p style={modalDescriptionStyle}>Media assets are reusable approved image, video or document references for WhatsApp template headers. Validate each asset before activation.</p>
        <div className="general-settings-control-grid">
          <TextField label="Media Key" value={payload.key} disabled={!canUpdate || saving || Boolean(asset)} onChange={(key) => setPayload((current) => ({ ...current, key }))} />
          <label style={labelStyle}>Version<input type="number" min={1} value={payload.version} disabled={!canUpdate || saving || Boolean(asset)} onChange={(event) => setPayload((current) => ({ ...current, version: Number(event.target.value) }))} style={inputStyle} /></label>
          <TextField label="Name" value={payload.name} disabled={!canUpdate || saving} onChange={(name) => setPayload((current) => ({ ...current, name }))} />
          <label style={labelStyle}>Media Type<select value={payload.mediaType} disabled={!canUpdate || saving} onChange={(event) => setPayload((current) => ({ ...current, mediaType: event.target.value as CommunicationMediaAssetContract['mediaType'] }))} style={inputStyle}><option value="IMAGE">IMAGE</option><option value="VIDEO">VIDEO</option><option value="DOCUMENT">DOCUMENT</option></select></label>
          <label style={labelStyle}>Source Type<select value={payload.sourceType} disabled={!canUpdate || saving} onChange={(event) => setPayload((current) => ({ ...current, sourceType: event.target.value as CommunicationMediaAssetContract['sourceType'] }))} style={inputStyle}><option value="PUBLIC_HTTPS_URL">PUBLIC_HTTPS_URL</option><option value="PROVIDER_MEDIA_ID">PROVIDER_MEDIA_ID</option></select></label>
          <TextField label="Public HTTPS URL" value={publicUrl} disabled={!canUpdate || saving || payload.sourceType !== 'PUBLIC_HTTPS_URL'} onChange={setPublicUrl} />
          <TextField label="Provider Media ID" value={providerMediaId} disabled={!canUpdate || saving || payload.sourceType !== 'PROVIDER_MEDIA_ID'} onChange={setProviderMediaId} />
          <TextField label="MIME Type" value={payload.mimeType || ''} disabled={!canUpdate || saving} onChange={(mimeType) => setPayload((current) => ({ ...current, mimeType }))} />
          <label style={labelStyle}>Size Bytes<input type="number" min={0} value={payload.fileSizeBytes || ''} disabled={!canUpdate || saving} onChange={(event) => setPayload((current) => ({ ...current, fileSizeBytes: event.target.value ? Number(event.target.value) : undefined }))} style={inputStyle} /></label>
          <label style={labelStyle}>Scope<select value={payload.scopeType} disabled={!canUpdate || saving} onChange={(event) => setPayload((current) => ({ ...current, scopeType: event.target.value as CommunicationMediaAssetContract['scopeType'] }))} style={inputStyle}><option value="GLOBAL">GLOBAL</option><option value="CENTER">CENTER</option></select></label>
          <TextField label="Scope Value" value={payload.scopeValue || ''} disabled={!canUpdate || saving || payload.scopeType !== 'CENTER'} onChange={(scopeValue) => setPayload((current) => ({ ...current, scopeValue }))} />
          <YesNoControl label="Active" value={payload.isActive} disabled={!canUpdate || saving} onChange={(isActive) => setPayload((current) => ({ ...current, isActive }))} />
        </div>
        <div style={modalActionsStyle}><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!canUpdate || saving} onClick={() => void save()}>{saving ? 'Saving...' : 'Save Media Asset'}</button></div>
      </div>
    </div>
  );
}

function MediaAssetPreview({ asset, usages, onClose }: { asset: CommunicationMediaAssetSummary; usages: string[]; onClose: () => void }) {
  return (
    <div className="modal-overlay" style={modalOverlayStyle}>
      <div className="modal-content admin-premium-modal" style={modalContentStyle}>
        <button onClick={onClose} className="admin-modal-close">&times;</button>
        <h3 style={modalTitleStyle}>Media Asset Preview</h3>
        <div className="workflow-preview-panel">
          <p><strong>Asset:</strong> {asset.name} ({asset.key} v{asset.version})</p>
          <p><strong>Type:</strong> {asset.mediaType} | <strong>Source:</strong> {asset.sourceType} | <strong>Status:</strong> {asset.validationStatus}</p>
          <p><strong>MIME:</strong> {asset.mimeType || '-'} | <strong>Size:</strong> {asset.fileSizeBytes ? `${Math.round(Number(asset.fileSizeBytes) / 1024)} KB` : '-'}</p>
          {asset.publicUrl ? <p><strong>Public URL:</strong> <a href={asset.publicUrl} target="_blank" rel="noreferrer">{asset.publicUrl}</a></p> : <p><strong>Public URL:</strong> Not available in summary. Edit the asset to view or update source details.</p>}
          {asset.providerMediaId && <p><strong>Provider Media ID:</strong> {asset.providerMediaId}</p>}
          <div className="general-settings-section">
            <div className="enterprise-card-heading"><div><strong>Workflow Usage</strong><span>{usages.length} reference{usages.length === 1 ? '' : 's'}</span></div></div>
            {usages.length ? <ul className="media-usage-list">{usages.map((usage) => <li key={usage}>{usage}</li>)}</ul> : <p className="general-settings-help">No loaded workflow currently references this media asset.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkflowCreateModal({
  groups,
  canUpdate,
  onClose,
  onCreate,
  onCreateOfferCampaign,
}: {
  groups: GeneralSettingsGroup[];
  canUpdate: boolean;
  onClose: () => void;
  onCreate: (payload: AutomationWorkflowCreatePayload) => Promise<void>;
  onCreateOfferCampaign: (payload: AutomationWorkflowCreatePayload) => Promise<void>;
}) {
  const [payload, setPayload] = useState<AutomationWorkflowCreatePayload>({ key: '', groupKey: groups[0]?.key || '', name: '', category: 'CUSTOM', isEnabled: false, manualPolicy: 'STRICT_SCHEDULE_ONLY', settings: [], steps: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isOfferCampaign = payload.groupKey === 'OFFER_CAMPAIGNS';
  const save = async () => {
    const normalizedKey = payload.key.trim().toUpperCase();
    const normalizedName = payload.name.trim();
    if (!normalizedKey) {
      setError('Workflow Key is required.');
      return;
    }
    if (!normalizedName) {
      setError('Workflow Name is required.');
      return;
    }
    if (!payload.groupKey) {
      setError('Workflow Group is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const normalizedPayload = { ...payload, key: normalizedKey, name: normalizedName };
      if (isOfferCampaign) await onCreateOfferCampaign(normalizedPayload);
      else await onCreate(normalizedPayload);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Workflow could not be created.');
    } finally {
      setSaving(false);
    }
  };
  return <div className="modal-overlay" style={modalOverlayStyle}><div className="modal-content admin-premium-modal" style={modalContentStyle}><button onClick={onClose} disabled={saving} aria-label="Close workflow editor" className="admin-modal-close">&times;</button><h3 style={modalTitleStyle}>Add Workflow</h3><div className="general-settings-control-grid"><TextField label="Workflow Key" value={payload.key} disabled={!canUpdate || saving} onChange={(key) => { setError(''); setPayload((current) => ({ ...current, key: key.toUpperCase() })); }} /><TextField label="Name" value={payload.name} disabled={!canUpdate || saving} onChange={(name) => { setError(''); setPayload((current) => ({ ...current, name })); }} /><label style={labelStyle}>Group<select value={payload.groupKey} disabled={!canUpdate || saving} onChange={(event) => { setError(''); setPayload((current) => ({ ...current, groupKey: event.target.value, category: event.target.value === 'OFFER_CAMPAIGNS' ? 'OFFER_CAMPAIGN' : current.category, isEnabled: event.target.value === 'OFFER_CAMPAIGNS' ? false : current.isEnabled })); }} style={inputStyle}>{groups.map((group) => <option key={group.key} value={group.key}>{group.name}</option>)}</select></label><TextField label="Category" value={payload.category} disabled={!canUpdate || saving || isOfferCampaign} onChange={(category) => setPayload((current) => ({ ...current, category }))} /><label style={labelStyle} className="user-modal-wide">Description<textarea value={payload.description || ''} disabled={!canUpdate || saving} onChange={(event) => setPayload((current) => ({ ...current, description: event.target.value }))} style={{ ...inputStyle, minHeight: 90 }} /></label><YesNoControl label="Enabled" value={Boolean(payload.isEnabled)} disabled={!canUpdate || saving || isOfferCampaign} onChange={(isEnabled) => setPayload((current) => ({ ...current, isEnabled }))} help={isOfferCampaign ? 'Offer campaign shells always start in Draft. Configure and validate the campaign before activation.' : undefined} />{!isOfferCampaign && <YesNoControl label="Allow Manual Override" value={payload.manualPolicy === 'ALLOW_MANUAL_OVERRIDE'} disabled={!canUpdate || saving} onChange={(value) => setPayload((current) => ({ ...current, manualPolicy: value ? 'ALLOW_MANUAL_OVERRIDE' : 'STRICT_SCHEDULE_ONLY' }))} />}{isOfferCampaign && <div className="offer-help-panel user-modal-wide"><strong>Offer campaign workflow</strong><span>This creates a Draft campaign shell. Configure Looker sources, field mappings, limits, discount rules, code pattern and display behavior after creation.</span></div>}</div>{error && <div className="admin-error-banner" role="alert">{error}</div>}<div style={modalActionsStyle}><button className="btn btn-outline" disabled={saving} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!canUpdate || saving || !payload.key.trim() || !payload.name.trim() || !payload.groupKey} onClick={() => void save()}>{saving ? 'Saving...' : 'Create Workflow'}</button></div></div></div>;
}

function GroupEditor({ group, canUpdate, onClose, onSave }: { group?: GeneralSettingsGroup; canUpdate: boolean; onClose: () => void; onSave: (payload: GeneralSettingsGroupPayload) => Promise<void> }) {
  const [payload, setPayload] = useState<GeneralSettingsGroupPayload>({ key: group?.key || '', name: group?.name || '', description: group?.description || '', isActive: group?.is_active ?? true, displayOrder: group?.display_order ?? 100, metadata: group?.metadata_json || {} });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    const key = String(payload.key || '').trim().toUpperCase();
    const name = payload.name.trim();
    if (!key) {
      setError('Group Key is required.');
      return;
    }
    if (!name) {
      setError('Group Name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({ ...payload, key, name });
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Group could not be saved.');
    } finally {
      setSaving(false);
    }
  };
  return <div className="modal-overlay" style={modalOverlayStyle}><div className="modal-content admin-premium-modal" style={modalContentStyle}><button onClick={onClose} disabled={saving} aria-label="Close group editor" className="admin-modal-close">&times;</button><h3 style={modalTitleStyle}>{group ? 'Edit Group' : 'Add Group'}</h3><div className="general-settings-control-grid"><TextField label="Group Key" value={payload.key || ''} disabled={!canUpdate || saving || Boolean(group)} onChange={(key) => { setError(''); setPayload((current) => ({ ...current, key: key.toUpperCase() })); }} /><TextField label="Name" value={payload.name} disabled={!canUpdate || saving} onChange={(name) => { setError(''); setPayload((current) => ({ ...current, name })); }} /><TextField label="Description" value={payload.description || ''} disabled={!canUpdate || saving} onChange={(description) => setPayload((current) => ({ ...current, description }))} /><label style={labelStyle}>Display Order<input type="number" min={1} value={payload.displayOrder || 100} disabled={!canUpdate || saving} onChange={(event) => setPayload((current) => ({ ...current, displayOrder: Number(event.target.value) }))} style={inputStyle} /></label><YesNoControl label="Active" value={Boolean(payload.isActive)} disabled={!canUpdate || saving} onChange={(isActive) => setPayload((current) => ({ ...current, isActive }))} /></div>{error && <div className="admin-error-banner" role="alert">{error}</div>}<div style={modalActionsStyle}><button className="btn btn-outline" disabled={saving} onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={!canUpdate || saving || !String(payload.key || '').trim() || !payload.name.trim()} onClick={() => void save()}>{saving ? 'Saving...' : 'Save Group'}</button></div></div></div>;
}

function YesNoControl({ label, value, disabled, onChange, help, compact }: { label: string; value: boolean; disabled: boolean; onChange: (value: boolean) => void; help?: string; compact?: boolean }) {
  return <label style={labelStyle} className={compact ? 'general-settings-compact-toggle' : undefined}>{label}<div className="general-settings-yes-no"><button type="button" className={value ? 'active' : ''} disabled={disabled} onClick={() => onChange(true)}>Yes</button><button type="button" className={!value ? 'active' : ''} disabled={disabled} onClick={() => onChange(false)}>No</button></div><HelpText text={help} /></label>;
}

function TextField({ label, value, disabled, onChange }: { label: string; value: string; disabled: boolean; onChange: (value: string) => void }) {
  return <label style={labelStyle}>{label}<input value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} style={inputStyle} /></label>;
}

function HelpText({ text }: { text?: string }) {
  return text ? <small className="general-settings-help">{text}</small> : null;
}

function activeTemplateOptions(settings: IntegrationSetting[]): TemplateOption[] {
  return settings
    .filter((setting) => {
      const kind = String(setting.setting_kind || '').toUpperCase();
      const approval = String(setting.approval_status || '').toUpperCase();
      const channel = String(setting.channel || '').toUpperCase();
      const hiddenStatuses = new Set(['REJECTED', 'DISABLED', 'INACTIVE', 'PAUSED']);
      return kind === 'TEMPLATE'
        && setting.is_active
        && setting.has_value
        && ['WHATSAPP', 'SMS', 'EMAIL'].includes(channel)
        && !hiddenStatuses.has(approval);
    })
    .map((setting) => {
      const channel = String(setting.channel || '').toUpperCase() as WorkflowChannel;
      const logicalCode = String(setting.logical_code || setting.key);
      const templateName = String(setting.masked_value || setting.logical_code || setting.key).trim();
      const approval = String(setting.approval_status || '').toUpperCase();
      const approvalLabel = approval ? ` - ${approval}` : '';
      return {
        key: setting.key,
        channel,
        logicalCode,
        templateName,
        languageCode: setting.language_code,
        variableSchema: setting.variable_schema_json,
        metadata: setting.metadata_json,
        label: `${setting.name || setting.key} (${templateName}${approvalLabel})`,
      };
    })
    .sort((left, right) => left.channel.localeCompare(right.channel) || left.templateName.localeCompare(right.templateName));
}

function automationTemplatesToOptions(templates: AutomationTemplateSummary[]): TemplateOption[] {
  return templates
    .map((template) => {
      const channel = String(template.channel || 'WHATSAPP').toUpperCase() as WorkflowChannel;
      const approval = String(template.approvalStatus || '').toUpperCase();
      const approvalLabel = approval ? ` - ${approval}` : '';
      return {
        key: template.key,
        channel,
        logicalCode: String(template.logicalCode || template.key),
        templateName: template.templateName,
        languageCode: template.languageCode,
        variableSchema: template.variableSchema,
        metadata: template.metadata,
        label: `${template.name || template.key} (${template.templateName}${approvalLabel})`,
      };
    })
    .sort((left, right) => left.channel.localeCompare(right.channel) || left.templateName.localeCompare(right.templateName));
}

function metadata(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? value as Record<string, unknown> : {};
}
function controlMeta(setting?: AutomationWorkflowSetting | null): ControlMeta {
  return metadata(setting?.validation_json) as ControlMeta;
}
function textMeta(value: unknown, fallback: string) {
  return typeof value === 'string' && value.trim() ? value : fallback;
}
function inferControl(setting: AutomationWorkflowSetting): ControlMeta['control'] {
  if (setting.setting_type === 'DURATION') return 'duration';
  if (setting.setting_type === 'BOOLEAN') return 'toggle';
  if (setting.setting_type === 'SELECT') return 'select';
  if (setting.setting_type === 'TEXT') return 'text';
  return 'number';
}
function groupedSettings(settings: AutomationWorkflowSetting[]) {
  const groups = new Map<string, AutomationWorkflowSetting[]>();
  for (const setting of settings) {
    const section = textMeta(controlMeta(setting).section, 'Workflow Settings');
    groups.set(section, [...(groups.get(section) || []), setting]);
  }
  return [...groups.entries()];
}
function durationValue(value: unknown): { unit: WorkflowDelayUnit; value: number } {
  const record = metadata(value);
  return { unit: String(record.unit || 'DAYS') as WorkflowDelayUnit, value: Number(record.value || 1) };
}
function readSettingValue(settings: AutomationWorkflowSetting[], key: string) {
  const value = metadata(settings.find((setting) => setting.setting_key === key)?.setting_value_json);
  return value.value;
}
function minFor(setting: AutomationWorkflowSetting) { return Number(controlMeta(setting).min || 1); }
function maxFor(setting: AutomationWorkflowSetting) { return Number(controlMeta(setting).max || 10); }
function clamp(value: number, min: number, max: number) { return Math.max(min, Math.min(max, value)); }
function selectOptions(meta: ControlMeta, value: unknown) {
  const options = meta.options || [];
  if (options.length) return options.map((option) => typeof option === 'string' ? { label: option, value: option } : option);
  const current = String(metadata(value).value || '');
  return current ? [{ label: current, value: current }] : [];
}
function channelOptions(meta: ControlMeta, steps: AutomationWorkflowStep[]): WorkflowChannel[] {
  const values = [...(meta.stepDefaults?.channels || []), ...(meta.stepDefaults?.channel ? [meta.stepDefaults.channel] : []), ...steps.map((step) => step.channel)];
  return [...new Set(values)].filter(Boolean) as WorkflowChannel[];
}
function normalizeStepCount(steps: AutomationWorkflowStep[], count: number, meta: ControlMeta) {
  const max = Number(meta.max || 10);
  const bounded = clamp(count, Number(meta.min || 1), max);
  const defaults = meta.stepDefaults || {};
  const current = [...steps].sort((a, b) => a.step_order - b.step_order);
  const previous = current[current.length - 1];
  for (let index = current.length + 1; index <= bounded; index += 1) {
    const keyPrefix = defaults.keyPrefix || 'STEP';
    const namePrefix = defaults.namePrefix || 'Step';
    const templatePrefix = defaults.templatePrefix || 'workflow_step';
    current.push({
      step_key: `${keyPrefix}_${index}`,
      step_name: `${namePrefix} ${index}`,
      step_order: index,
      trigger_type: index === 1 ? defaults.triggerTypeFirst || 'EVENT' : defaults.triggerTypeNext || previous?.trigger_type || 'EVENT',
      delay_unit: previous?.delay_unit || 'DAYS',
      delay_value: previous?.delay_value || 1,
      channel: defaults.channel || previous?.channel || 'WHATSAPP',
      template_setting_key: defaults.templateSettingKey || previous?.template_setting_key || null,
      template_name: `${templatePrefix}_${index}`,
      is_active: true,
      metadata_json: { attempt: index },
    });
  }
  return current.filter((step) => step.step_order <= bounded).map((step) => ({ ...step, is_active: step.step_order <= bounded ? step.is_active : false }));
}
function toStepPayload(step: AutomationWorkflowStep): NonNullable<AutomationWorkflowPayload['steps']>[number] {
  return { stepKey: step.step_key, stepName: step.step_name, stepOrder: step.step_order, triggerType: step.trigger_type, delayUnit: step.delay_unit, delayValue: step.delay_value, channel: step.channel, templateSettingKey: step.template_setting_key || undefined, templateName: step.template_name || undefined, isActive: step.is_active, automationContract: stepContract(step) };
}
function calculatePreview(settings: AutomationWorkflowSetting[], steps: AutomationWorkflowStep[]) {
  const base = new Date();
  const start = durationValue(settings.find((setting) => setting.setting_key === 'START_AFTER_VISIT')?.setting_value_json);
  const gap = durationValue(settings.find((setting) => setting.setting_key === 'WAIT_BETWEEN_REMINDERS')?.setting_value_json);
  const expiry = durationValue(settings.find((setting) => setting.setting_key === 'LINK_EXPIRY_AFTER_SEND')?.setting_value_json);
  let previous = base;
  const rows = steps.filter((step) => step.is_active).sort((a, b) => a.step_order - b.step_order).map((step) => {
    previous = addDuration(previous, step.step_order === 1 ? start.unit : gap.unit, step.step_order === 1 ? start.value : gap.value);
    return { label: step.step_name, date: previous.toLocaleString() };
  });
  return [...rows, { label: 'Link Expires', date: addDuration(previous, expiry.unit, expiry.value).toLocaleString() }];
}
function addDuration(date: Date, unit: WorkflowDelayUnit, value: number) {
  const next = new Date(date);
  if (unit === 'SECONDS') next.setSeconds(next.getSeconds() + value);
  if (unit === 'MINUTES') next.setMinutes(next.getMinutes() + value);
  if (unit === 'HOURS') next.setHours(next.getHours() + value);
  if (unit === 'DAYS') next.setDate(next.getDate() + value);
  if (unit === 'WEEKS') next.setDate(next.getDate() + value * 7);
  if (unit === 'MONTHS') next.setMonth(next.getMonth() + value);
  if (unit === 'YEARS') next.setFullYear(next.getFullYear() + value);
  return next;
}

const conditionOperators: AutomationConditionOperator[] = [
  'EQUALS',
  'NOT_EQUALS',
  'CONTAINS',
  'NOT_CONTAINS',
  'STARTS_WITH',
  'ENDS_WITH',
  'EXISTS',
  'MISSING',
  'GREATER_THAN',
  'GREATER_THAN_OR_EQUAL',
  'LESS_THAN',
  'LESS_THAN_OR_EQUAL',
  'BETWEEN',
  'IN',
  'NOT_IN',
  'DATE_BEFORE',
  'DATE_AFTER',
  'DATE_TODAY',
  'DATE_TOMORROW',
  'DATE_YESTERDAY',
  'DATE_WITHIN_NEXT_DAYS',
  'DATE_WITHIN_PREVIOUS_DAYS',
];
const noValueConditionOperators = new Set<AutomationConditionOperator>(['EXISTS', 'MISSING', 'DATE_TODAY', 'DATE_TOMORROW', 'DATE_YESTERDAY']);
const numericConditionOperators = new Set<AutomationConditionOperator>(['GREATER_THAN', 'GREATER_THAN_OR_EQUAL', 'LESS_THAN', 'LESS_THAN_OR_EQUAL', 'DATE_WITHIN_NEXT_DAYS', 'DATE_WITHIN_PREVIOUS_DAYS']);
const dateConditionOperators = new Set<AutomationConditionOperator>(['DATE_BEFORE', 'DATE_AFTER']);
const arrayConditionOperators = new Set<AutomationConditionOperator>(['IN', 'NOT_IN', 'BETWEEN']);
const bindingSources: TemplateBindingSource[] = ['TRIGGER_FIELD', 'ENRICHMENT_FIELD', 'STATIC_VALUE', 'COMPUTED_VALUE', 'MEDIA_ASSET', 'MEDIA_URL_FIELD', 'PROVIDER_MEDIA_ID'];
const computedTemplateValues = [
  { value: 'CURRENT_DATE', label: 'Current date' },
  { value: 'CURRENT_DATETIME', label: 'Current date and time' },
  { value: 'FORMATTED_TRIGGER_DATE', label: 'Formatted trigger date' },
  { value: 'CENTER_DISPLAY_NAME', label: 'Center display name' },
  { value: 'PATIENT_INITIALS', label: 'Patient initials' },
];

function defaultTrigger(sources: AutomationDataSourceSummary[]): AutomationTriggerContract {
  return { sourceKey: sources[0]?.key || '', triggerMode: 'ON_NEW_EVENT', anchorField: 'trigger.registrationTimestamp', enrichments: [] };
}
function normalizeTriggerResponse(value: AutomationTriggerContract & { enrichments?: unknown }): AutomationTriggerContract {
  const rawEnrichments = metadata(value.enrichments);
  const sources = Array.isArray(rawEnrichments.sources) ? rawEnrichments.sources : value.enrichments;
  return { ...value, enrichments: Array.isArray(sources) ? sources as NonNullable<AutomationTriggerContract['enrichments']> : [] };
}
function fieldOptions(source?: AutomationDataSourceSummary, trigger?: AutomationTriggerContract, lookerFields: ConfiguredLookerFieldPreview[] = []): string[] {
  const mappedFields = Object.entries(source?.fieldMappings ?? {}).flatMap(([alias, field]) => [`trigger.${alias}`, lookerFieldPath(field)]);
  const queryFields = (source?.query?.fields ?? []).map(lookerFieldPath);
  const previewFields = lookerFields.map((field) => lookerFieldPath(field.fieldKey));
  const base = ['trigger.eventKey', 'trigger.subjectKey', 'trigger.occurredAt', 'trigger.patientId', 'trigger.mrn', 'trigger.patientFullName', 'trigger.center', 'trigger.whatsappPhone', 'trigger.registrationTimestamp'];
  const inferred = source ? [`source.${source.key}.eventKey`, `source.${source.key}.center`] : [];
  const enrichment = (trigger?.enrichments || []).flatMap((item) => [`enrichments.${item.alias}.${item.enrichmentField}`, `enrichments.${item.alias}.value`]);
  return [...new Set([...base, ...mappedFields, ...queryFields, ...previewFields, ...inferred, ...enrichment].filter(Boolean))];
}
function lookerFieldPath(field: unknown): string {
  const raw = lookerFieldKey(field);
  if (!raw) return '';
  return `trigger.${raw.replace(/[^A-Za-z0-9_.]/g, '_').replace(/(^|\.)([^A-Za-z_])/g, '$1_$2')}`;
}
function lookerFieldKey(field: unknown): string {
  if (typeof field === 'string') return field.trim();
  if (!field || typeof field !== 'object' || Array.isArray(field)) return '';
  const record = field as Record<string, unknown>;
  const candidates = [record.fieldKey, record.field, record.name, record.key, record.value, record.path];
  const match = candidates.find((candidate) => typeof candidate === 'string' && candidate.trim());
  return typeof match === 'string' ? match.trim() : '';
}
function conditionGroup(value?: AutomationConditionNode): Extract<AutomationConditionNode, { type: 'GROUP' }> {
  if (value?.type === 'GROUP') return value;
  return { type: 'GROUP', combinator: 'AND', conditions: value ? [value] : [] };
}
function defaultPredicate(field = 'trigger.registrationTimestamp'): Extract<AutomationConditionNode, { type: 'PREDICATE' }> {
  return { type: 'PREDICATE', field, operator: 'EXISTS' };
}
function updatePredicate(group: Extract<AutomationConditionNode, { type: 'GROUP' }>, index: number, patch: Partial<Extract<AutomationConditionNode, { type: 'PREDICATE' }>>): Extract<AutomationConditionNode, { type: 'GROUP' }> {
  return { ...group, conditions: group.conditions.map((item, itemIndex) => itemIndex === index ? { ...defaultPredicate(), ...(item.type === 'PREDICATE' ? item : {}), ...patch } : item) };
}
function predicateValueText(value: unknown, operator?: AutomationConditionOperator): string {
  if (dateConditionOperators.has(operator as AutomationConditionOperator) && typeof value === 'string') return toLocalDatetimeValue(value);
  return Array.isArray(value) ? value.join(', ') : value === undefined || value === null ? '' : String(value);
}
function parsePredicateValue(value: string, operator?: AutomationConditionOperator): AutomationConditionValue | undefined {
  if (noValueConditionOperators.has(operator as AutomationConditionOperator)) return undefined;
  if (dateConditionOperators.has(operator as AutomationConditionOperator)) return value ? new Date(value).toISOString() : '';
  if (arrayConditionOperators.has(operator as AutomationConditionOperator)) {
    return value.split(',').map((item) => {
      const trimmed = item.trim();
      const numeric = Number(trimmed);
      return trimmed !== '' && Number.isFinite(numeric) ? numeric : trimmed;
    }).filter((item) => item !== '');
  }
  const numeric = Number(value);
  return value.trim() !== '' && Number.isFinite(numeric) && numericConditionOperators.has(operator as AutomationConditionOperator) ? numeric : value;
}
function defaultConditionValue(operator: AutomationConditionOperator): AutomationConditionValue | undefined {
  if (noValueConditionOperators.has(operator)) return undefined;
  if (arrayConditionOperators.has(operator)) return [];
  if (numericConditionOperators.has(operator)) return 0;
  if (dateConditionOperators.has(operator)) return new Date().toISOString();
  return '';
}
function conditionInputType(operator: AutomationConditionOperator): string {
  if (numericConditionOperators.has(operator)) return 'number';
  if (dateConditionOperators.has(operator)) return 'datetime-local';
  return 'text';
}
function conditionOperatorsForField(field: string, previews: ConfiguredLookerFieldPreview[] = []): AutomationConditionOperator[] {
  const preview = lookerPreviewForField(field, previews);
  const type = String(preview?.type || '').toLowerCase();
  if (preview?.isDateTimeCandidate || type.includes('date') || type.includes('time')) {
    return ['EXISTS', 'MISSING', 'DATE_BEFORE', 'DATE_AFTER', 'DATE_TODAY', 'DATE_TOMORROW', 'DATE_YESTERDAY', 'DATE_WITHIN_NEXT_DAYS', 'DATE_WITHIN_PREVIOUS_DAYS', 'EQUALS', 'NOT_EQUALS'];
  }
  if (type.includes('number') || type.includes('int') || type.includes('decimal') || type.includes('count')) {
    return ['EXISTS', 'MISSING', 'EQUALS', 'NOT_EQUALS', 'GREATER_THAN', 'GREATER_THAN_OR_EQUAL', 'LESS_THAN', 'LESS_THAN_OR_EQUAL', 'BETWEEN', 'IN', 'NOT_IN'];
  }
  return conditionOperators;
}
function conditionSampleValues(field: string, previews: ConfiguredLookerFieldPreview[] = []): string[] {
  const preview = lookerPreviewForField(field, previews);
  const value = preview?.sampleValue;
  if (value === undefined || value === null || value === '') return [];
  return [String(value)];
}
function lookerPreviewForField(field: string, previews: ConfiguredLookerFieldPreview[]) {
  const normalized = field.replace(/^trigger\./, '').replaceAll('_', '.').toLowerCase();
  return previews.find((preview) => {
    const key = preview.fieldKey.toLowerCase();
    return field.toLowerCase().endsWith(key) || key.endsWith(normalized) || lookerFieldPath(preview.fieldKey).toLowerCase() === field.toLowerCase();
  });
}
function stableId(value: string) {
  return value.replace(/[^A-Za-z0-9_-]/g, '-');
}
function conditionPlaceholder(operator: AutomationConditionOperator): string {
  if (operator === 'BETWEEN') return 'Start, End';
  if (operator === 'IN' || operator === 'NOT_IN') return 'Comma-separated values';
  if (operator === 'DATE_WITHIN_NEXT_DAYS' || operator === 'DATE_WITHIN_PREVIOUS_DAYS') return 'Number of days';
  if (dateConditionOperators.has(operator)) return 'Date and time';
  return 'Value';
}
function conditionOperatorLabel(operator: AutomationConditionOperator): string {
  const labels: Record<AutomationConditionOperator, string> = {
    EQUALS: 'Equals',
    NOT_EQUALS: 'Not equals',
    IN: 'In list',
    NOT_IN: 'Not in list',
    CONTAINS: 'Contains',
    NOT_CONTAINS: 'Does not contain',
    STARTS_WITH: 'Starts with',
    ENDS_WITH: 'Ends with',
    EXISTS: 'Is not empty',
    MISSING: 'Is empty',
    GREATER_THAN: 'Greater than',
    GREATER_THAN_OR_EQUAL: 'Greater than or equal',
    LESS_THAN: 'Less than',
    LESS_THAN_OR_EQUAL: 'Less than or equal',
    BETWEEN: 'Between',
    DATE_BEFORE: 'Date before',
    DATE_AFTER: 'Date after',
    DATE_TODAY: 'Date is today',
    DATE_TOMORROW: 'Date is tomorrow',
    DATE_YESTERDAY: 'Date is yesterday',
    DATE_WITHIN_NEXT_DAYS: 'Date within next days',
    DATE_WITHIN_PREVIOUS_DAYS: 'Date within previous days',
  };
  return labels[operator];
}
function updateArrayItem<T>(items: T[], index: number, value: T): T[] {
  return items.map((item, itemIndex) => itemIndex === index ? value : item);
}
function stepContract(step: AutomationWorkflowStep): AutomationWorkflowStepContract {
  const raw = metadata(step.metadata_json);
  const contract = metadata(raw.contract) as Partial<AutomationWorkflowStepContract>;
  return {
    stepKey: contract.stepKey || step.step_key,
    stepName: contract.stepName || step.step_name,
    stepOrder: contract.stepOrder || step.step_order,
    isActive: contract.isActive ?? step.is_active,
    timing: {
      anchor: contract.timing?.anchor || (step.trigger_type === 'PREVIOUS_STEP_OFFSET' ? 'PREVIOUS_STEP_COMPLETED_AT' : 'TRIGGER_TIMESTAMP'),
      delayUnit: contract.timing?.delayUnit || step.delay_unit,
      delayValue: Number(contract.timing?.delayValue ?? step.delay_value ?? 0),
      direction: contract.timing?.direction || 'AFTER',
      fixedTime: contract.timing?.fixedTime || undefined,
    },
    condition: contract.condition,
    action: {
      actionType: 'SEND_WHATSAPP_TEMPLATE',
      channel: 'WHATSAPP',
      templateSettingKey: contract.action?.templateSettingKey || step.template_setting_key || '',
      templateName: contract.action?.templateName || step.template_name || undefined,
      languageCode: contract.action?.languageCode,
      bindings: contract.action?.bindings || [],
      retry: contract.action?.retry || defaultRetry(),
      dependency: contract.action?.dependency || 'PREVIOUS_STEP_SUCCESS',
      stop: contract.action?.stop || defaultStop(),
      suppression: contract.action?.suppression || defaultSuppression(),
    },
  };
}
function ensureStepContract(step: AutomationWorkflowStep): AutomationWorkflowStep {
  const contract = stepContract(step);
  return { ...step, metadata_json: { ...metadata(step.metadata_json), contract } };
}
function onChangeContract(onChange: (patch: Partial<AutomationWorkflowStep>) => void, step: AutomationWorkflowStep, contract: AutomationWorkflowStepContract, patch: Partial<AutomationWorkflowStep> = {}) {
  onChange({
    ...patch,
    step_key: contract.stepKey,
    step_name: contract.stepName,
    step_order: contract.stepOrder,
    is_active: contract.isActive,
    trigger_type: contract.timing.anchor === 'PREVIOUS_STEP_COMPLETED_AT' ? 'PREVIOUS_STEP_OFFSET' : 'EVENT',
    delay_unit: contract.timing.delayUnit as WorkflowDelayUnit,
    delay_value: contract.timing.delayValue,
    channel: contract.action.channel as WorkflowChannel,
    template_setting_key: contract.action.templateSettingKey || step.template_setting_key,
    template_name: contract.action.templateName || step.template_name,
    metadata_json: { ...metadata(step.metadata_json), contract },
  });
}
function defaultRetry() {
  return { maxAttempts: 3, strategy: 'EXPONENTIAL' as const, initialDelaySeconds: 300, maxDelaySeconds: 3600, retryableFailureCodes: ['PROVIDER_UNAVAILABLE', 'PROVIDER_TIMEOUT_UNKNOWN'] };
}
function defaultStop(): AutomationStopPolicy {
  return { stopOnConfirmedFailure: false };
}
function defaultSuppression(): AutomationSuppressionPolicy {
  return { requireWhatsappPhone: true, respectPatientOptOut: true, suppressDuplicateTemplateWithinSeconds: 86400 };
}
function newStep(order: number, template?: TemplateOption): AutomationWorkflowStep {
  return ensureStepContract({
    step_key: `STEP_${order}`,
    step_name: `Step ${order}`,
    step_order: order,
    trigger_type: order === 1 ? 'EVENT' : 'PREVIOUS_STEP_OFFSET',
    delay_unit: order === 1 ? 'DAYS' : 'DAYS',
    delay_value: order === 1 ? 1 : 1,
    channel: template?.channel || 'WHATSAPP',
    template_setting_key: template?.key || null,
    template_name: template?.templateName || null,
    is_active: true,
    metadata_json: {},
  });
}
function cloneStep(step: AutomationWorkflowStep, order: number): AutomationWorkflowStep {
  const clone = ensureStepContract({ ...step, step_key: `${step.step_key}_COPY_${order}`, step_name: `${step.step_name} Copy`, step_order: order });
  return { ...clone, metadata_json: { contract: { ...stepContract(clone), stepKey: clone.step_key, stepName: clone.step_name, stepOrder: order } } };
}
function reorderSteps(steps: AutomationWorkflowStep[]): AutomationWorkflowStep[] {
  return steps.sort((a, b) => a.step_order - b.step_order).map((step, index) => {
    const order = index + 1;
    const contract = { ...stepContract(step), stepOrder: order };
    return ensureStepContract({ ...step, step_order: order, metadata_json: { ...metadata(step.metadata_json), contract } });
  });
}
function moveStep(steps: AutomationWorkflowStep[], key: string, direction: -1 | 1): AutomationWorkflowStep[] {
  const ordered = [...steps].sort((a, b) => a.step_order - b.step_order);
  const index = ordered.findIndex((step) => step.step_key === key);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= ordered.length) return ordered;
  [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
  return reorderSteps(ordered);
}
function templateSlots(template: TemplateOption): TemplateComponentAddress[] {
  const schema = metadata(template.variableSchema);
  const metadataJson = metadata(template.metadata);
  const rawSlots = [schema.slots, schema.components, metadataJson.slots, metadataJson.components].find(Array.isArray);
  if (!Array.isArray(rawSlots)) return [];
  return rawSlots.flatMap((slot, index) => normalizeTemplateSlot(slot, index));
}
function normalizeTemplateSlot(slot: unknown, index: number): TemplateComponentAddress[] {
  const record = metadata(slot);
  const component = String(record.component || record.type || 'BODY').toUpperCase() as TemplateComponentAddress['component'];
  const parameters = Array.isArray(record.parameters) ? record.parameters : Array.isArray(record.variables) ? record.variables : [record];
  return parameters.map((parameter, parameterIndex) => {
    const parameterRecord = metadata(parameter);
    const parameterType = String(parameterRecord.parameterType || parameterRecord.type || (component === 'HEADER' ? 'TEXT' : 'TEXT')).toUpperCase() as TemplateParameterType;
    return { component, componentIndex: Number(record.componentIndex ?? index), parameterIndex: Number(parameterRecord.parameterIndex ?? parameterIndex), parameterType, buttonType: parameterRecord.buttonType as TemplateComponentAddress['buttonType'], buttonIndex: Number(parameterRecord.buttonIndex ?? 0) };
  });
}
function defaultBinding(address: TemplateComponentAddress, firstField = 'trigger.patientFullName'): TemplateParameterBinding {
  const mediaSource: TemplateBindingSource = address.parameterType === 'TEXT' ? 'TRIGGER_FIELD' : 'MEDIA_ASSET';
  return { address, source: mediaSource, sourcePath: mediaSource === 'TRIGGER_FIELD' ? firstField : undefined, required: true };
}
function bindingKey(address: TemplateComponentAddress): string {
  return `${address.component}:${address.componentIndex}:${address.parameterIndex}:${address.parameterType}:${address.buttonIndex ?? ''}`;
}
function templateBindingLabel(address: TemplateComponentAddress): string {
  const button = address.component === 'BUTTON' ? ` Button ${Number(address.buttonIndex ?? 0) + 1}` : '';
  return `${address.component}${button} ${address.parameterType} variable ${Number(address.parameterIndex ?? 0) + 1}`;
}
function toLocalDatetimeValue(value?: string | Date | null): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
function sampleContext(trigger: AutomationTriggerContract) {
  return { [trigger.anchorField]: new Date().toISOString(), trigger: { subjectKey: 'SAMPLE-ROW', eventKey: 'SAMPLE-EVENT', patientFullName: 'Sample Subject', mrn: 'SAMPLE-MRN', center: 'Sample Center', whatsappPhone: '9999999999', registrationTimestamp: new Date().toISOString(), occurredAt: new Date().toISOString() } };
}
function friendlyUnit(unit: string): string {
  const labels: Record<string, string> = { SECONDS: 'Seconds', MINUTES: 'Minutes', HOURS: 'Hours', DAYS: 'Days', WEEKS: 'Weeks', MONTHS: 'Months', YEARS: 'Years' };
  return labels[unit] || unit;
}
function friendlyFieldLabel(field: string): string {
  const labels: Record<string, string> = {
    'trigger.registrationTimestamp': 'Registration date/time',
    'trigger.occurredAt': 'Event date/time',
    'trigger.subjectKey': 'Source row key',
    'trigger.eventKey': 'Event key',
    'trigger.patientFullName': 'Patient / subject full name',
    'trigger.mrn': 'MRN',
    'trigger.center': 'Center',
    'trigger.whatsappPhone': 'WhatsApp mobile number',
    'trigger.patientId': 'Patient ID',
  };
  return labels[field] || field.replace(/^trigger\./, '').replace(/^enrichments\./, 'Extra data: ').split('.').pop()?.replaceAll('_', ' ') || field;
}
function messageSummary(contract: AutomationWorkflowStepContract, template?: TemplateOption): string {
  const anchor = contract.timing.anchor === 'PREVIOUS_STEP_COMPLETED_AT' ? 'after previous message' : 'from selected trigger time';
  const templateName = template?.templateName || contract.action.templateName || 'no template selected';
  const direction = contract.timing.direction === 'BEFORE' ? 'before' : 'after';
  const fixed = contract.timing.fixedTime ? ` at ${contract.timing.fixedTime} IST` : '';
  return `Send ${contract.timing.delayValue} ${friendlyUnit(contract.timing.delayUnit).toLowerCase()} ${direction} ${anchor}${fixed} using ${templateName}`;
}
function PreviewJson({ title, value }: { title: string; value: unknown }) {
  return value ? <pre className="general-settings-json-preview">{title}: {JSON.stringify(value, null, 2)}</pre> : null;
}
function TemplateBindingPreview({ steps }: { steps: AutomationWorkflowStep[] }) {
  const summary = steps.map((step) => ({ step: step.step_key, template: step.template_name, bindings: stepContract(step).action.bindings.length }));
  return <pre className="general-settings-json-preview">Template Binding Preview: {JSON.stringify(summary, null, 2)}</pre>;
}

const modalOverlayStyle: CSSProperties = { display: 'flex', position: 'fixed', inset: 0, padding: 16, background: 'rgba(15, 23, 42, 0.62)', backdropFilter: 'blur(8px)', zIndex: 99999, alignItems: 'center', justifyContent: 'center', overflowY: 'auto' };
const modalContentStyle: CSSProperties = { width: 'min(980px, 100%)', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', background: 'var(--surface-solid)', border: '1px solid var(--border)', borderRadius: 20, padding: 'clamp(18px, 3vw, 28px)', boxShadow: 'var(--shadow-lg)', position: 'relative', display: 'grid', gap: 18 };
const modalTitleStyle: CSSProperties = { margin: 0, fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(20px, 4vw, 26px)', fontWeight: 900, color: 'var(--text)' };
const modalDescriptionStyle: CSSProperties = { margin: '8px 0 0', color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6 };
const labelStyle: CSSProperties = { display: 'grid', gap: 8, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: 11 };
const inputStyle: CSSProperties = { minHeight: 42, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', padding: '9px 11px', fontWeight: 650, minWidth: 0 };
const previewGridStyle: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 };
const modalActionsStyle: CSSProperties = { display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' };
